import Phaser from 'phaser';
import { TILE_SIZE, TileType } from '@game/protocol';

import {
  addGridTexture,
  celLight,
  celShadow,
  cloud,
  clipped,
  context2d,
  createCanvas,
  ellipse,
  px,
  roundRect,
  shade,
  type Shape,
} from './canvas.ts';
import { TILE_PALETTE } from './palette.ts';

/**
 * Tileset do terreno (PRD §8.4).
 *
 * ## Por que quatro texturas, e não uma
 *
 * O cenário é montado em camadas, e cada camada consome uma textura própria:
 *
 * | textura    | camada     | o que carrega                                    |
 * |------------|------------|--------------------------------------------------|
 * | `tileset`  | solo       | grama, terra, areia, calçamento, rocha, água     |
 * | `edges`    | transição  | a borda irregular de um material sobre o vizinho |
 * | `shade`    | sombra     | a sombra que parede e árvore projetam no chão    |
 * | `props`    | vegetação  | árvore, pedra e moita, em duas metades           |
 *
 * A transição é o que tira do mapa o aspecto de tabuleiro. Sem ela, grama encontra terra
 * numa linha reta de 32 px — e o olho lê a grade antes de ler o terreno. Com ela, cada
 * material **transborda** sobre o de baixo por uma borda recortada pixel a pixel, e a
 * grade some. É a mesma razão de existir da espuma na margem da água, generalizada para
 * todo par de materiais.
 *
 * As bordas não são desenhadas uma a uma: para cada material há **oito peças** — quatro
 * lados e quatro cantos — e as 256 combinações de vizinhança saem de compor essas peças.
 * Um autotile completo desenhado à mão custaria 47 artes por material; gerado por
 * composição, custa um laço.
 *
 * ## Duas metades para a vegetação
 *
 * Uma árvore de 32 px é um arbusto. A copa precisa transbordar para o tile de trás, e
 * uma camada de tilemap só sabe desenhar um quadro por célula — então a árvore é cortada
 * ao meio: a base vai na célula da árvore, e a copa vai na célula **de cima**, numa
 * segunda camada. Duas camadas custam dois quadros por árvore e mantêm o culling de
 * câmera que o Phaser faz de graça em tilemap; um sprite por árvore custaria um objeto
 * de cena por tronco do mapa inteiro.
 *
 * A água e o portal são **animados**, e a animação vive na textura, não nos tiles: a cada
 * fase o canvas é redesenhado e re-enviado à GPU com `refresh()`. Um upload de uma textura
 * pequena anima todos os tiles de água em tela ao mesmo tempo, sem um `putTileAt` sequer.
 */

export const TILESET_TEXTURE = 'tileset';
export const EDGES_TEXTURE = 'tileset-edges';
export const SHADE_TEXTURE = 'tileset-shade';
export const PROPS_TEXTURE = 'tileset-props';

export const VARIANTS = 6;

/** Fases da animação da água/portal. Poucas de propósito: pixel art anima em saltos. */
const PHASES = 8;
const PHASE_MS = 220;

const TAU = Math.PI * 2;

// --- Materiais de solo -------------------------------------------------------

/**
 * Materiais que se misturam entre si. A ordem **é** a prioridade: um material só
 * transborda sobre outro de índice menor.
 *
 * Água por último porque ela molha tudo que toca; grama primeiro porque é o fundo do
 * mundo. Terra e rocha de caverna empatariam — nunca se encontram no mesmo mapa, então a
 * ordem entre as duas não chega a ser exercitada.
 */
export const Material = {
  Grass: 0,
  Dirt: 1,
  Cave: 2,
  Sand: 3,
  Pavement: 4,
  Water: 5,
  /**
   * Lava. Última, então ela transborda sobre tudo — inclusive sobre a água.
   *
   * A prioridade não é arbitrária: o que a lava faz na borda é **iluminar** o material
   * vizinho, e uma poça de lava com a rocha derramando por cima dela leria como cratera
   * apagada. Ver `drawLava`.
   */
  Lava: 6,
} as const;

export type Material = (typeof Material)[keyof typeof Material];

const MATERIAL_COUNT = 7;

/**
 * Blocos da textura de solo: os materiais, mais o piso trabalhado e a pedra.
 *
 * ## Por que parede tem dois blocos, e não um
 *
 * A versão anterior desenhava **toda** parede com a faixa clara no alto e a escura no pé,
 * que é o que dá altura a um bloco numa projeção sem perspectiva. Funciona para uma parede
 * de um tile. Numa muralha de três tiles de altura, funciona três vezes — e o que se via
 * era uma pilha de blocos listrados, com uma linha clara e uma escura a cada 32 px. Era a
 * principal fonte do "está tudo muito quadrado": a arte estava desenhando a **grade**, e
 * não a parede.
 *
 * Agora o topo iluminado só existe na **coroa**, que é o tile de parede sem parede acima
 * dele. O resto da massa usa o corpo, que não tem faixa nenhuma e emenda sem costura com o
 * de cima e o de baixo. Uma muralha alta passa a ler como uma superfície contínua com uma
 * borda iluminada em cima — que é o que ela é.
 *
 * Quem decide é `Terrain`, olhando um vizinho: o de cima. Nada disso trafega no protocolo.
 */
const GroundBlock = {
  ...Material,
  StairsUp: 7,
  StairsDown: 8,
  Portal: 9,
  /** Alvenaria. Dez quadros seguidos; ver `faceOffset`. */
  Wall: 10,
  /** Rocha bruta: encosta de montanha e parede de caverna. Dez quadros também. */
  Cliff: 20,
  /** Rocha com veio de minério. Dez quadros, como as outras duas faces de parede. */
  Ore: 30,
  /** Piso consagrado da zona de proteção. Ver `drawSanctuary`. */
  Sanctuary: 40,
  /** Rocha do subsolo coberta de ossada. Ver `drawBoneFloor`. */
  Bones: 41,

  // --- Mecanismos (PRD §15.2, §15.4) ---
  //
  // Os tiles de mecanismo entraram no protocolo em §15.2 e **nunca foram desenhados**:
  // `materialOf` devolvia `null` para todos eles, `groundFrame` caía no material do
  // vizinho, e o resultado é o que se via em jogo — uma porta que aparece como calçamento,
  // um buraco que aparece como chão de caverna, uma alavanca invisível. O jogador só
  // descobria a porta esbarrando nela.
  //
  // Eram seis: o sexto era o gancho de corda, e ele saiu do jogo junto com a corda. Os
  // números fecharam atrás dele — bloco de arte não trafega e não é gravado, então aqui a
  // renumeração não custa nada (diferente de `TileType`, onde o 18 ficou vago).
  //
  // Um tile que muda o mundo e não tem desenho é uma regra que o jogador não tem como
  // perceber, que é a classe de defeito que §11 inteiro passou consertando.
  DoorClosed: 42,
  DoorOpen: 43,
  DoorLocked: 44,
  Hole: 45,
  Lever: 46,
  Chest: 47,

  /**
   * Calçada de pedra da rua (PRD §15.8).
   *
   * Bloco próprio e material compartilhado: ela mistura como calçamento — mesma borda com a
   * grama, mesmos 256 quadros de transição — e desenha seixo miúdo em vez de laje cortada.
   * É a diferença que faz a rua não ser o chão da loja visto de fora.
   */
  Cobble: 48,
} as const;

const GROUND_BLOCKS = 49;

/**
 * A forma do coroamento — a faixa clara do topo da parede (PRD §15.8).
 *
 * ## O defeito que este tipo existe para consertar
 *
 * O coroamento era um booleano: "não há parede acima, então desenhe a faixa clara de ponta a
 * ponta do tile". Numa muralha reta isso está certo. Numa **encosta em diagonal** — que é o
 * contorno da esmagadora maioria da rocha de caverna e de serra — cada tile da escadinha
 * ganhava a mesma faixa clara horizontal de 32 px, e o resultado é a queixa que se lê na
 * tela: uma rocha que parece um lance de escada de blocos empilhados. A silhueta gerada era
 * uma diagonal; o **desenho** é que a devolvia como degrau, porque cada degrau vinha com uma
 * régua luminosa em cima.
 *
 * O conserto é olhar dois vizinhos a mais. Se a rocha continua para cima **à esquerda**, o
 * topo à vista deste tile não chega à borda oeste — ali o que se vê é face, na mesma altura
 * da face deste tile. Então a faixa **morre** naquele lado, e a que sobra emenda com a do
 * degrau vizinho: o que era uma pilha de barras horizontais vira uma crista contínua descendo
 * em diagonal, que é o que uma encosta é.
 */
export const Crown = {
  /** Há parede acima: este tile é miolo de massa e não tem topo à vista. */
  None: 0,
  /** Topo à vista dos dois lados. A faixa atravessa o tile — é a muralha reta. */
  Flat: 1,
  /** A rocha sobe a oeste: a faixa nasce do nada na borda esquerda. */
  RisesWest: 2,
  /** A rocha sobe a leste. */
  RisesEast: 3,
  /** Entalhe: a rocha sobe dos dois lados e este tile é o fundo da fenda. */
  Notch: 4,
} as const;

export type Crown = (typeof Crown)[keyof typeof Crown];

const CROWN_SHAPES = 5;

/**
 * Qual dos dez quadros de face usar.
 *
 * `crown` é a forma do topo à vista (ver `Crown`); `foot` é "não há parede abaixo" e manda
 * desenhar a sombra de contato com o chão. Nenhum dos dois dá o miolo de uma muralha alta,
 * que emenda sem costura acima e abaixo.
 *
 * O pé precisou de um bit próprio pela mesma razão que o topo: desenhado em **todo** tile
 * de parede, ele reaparece a cada 32 px como uma faixa escura, e o que era para ser a
 * sombra do chão vira a linha de uma grade. Numa muralha de quatro tiles era o defeito
 * mais visível que restava depois de o coroamento ter sido resolvido.
 */
function faceOffset(crown: Crown, foot: boolean): number {
  return crown * 2 + (foot ? 1 : 0);
}

/**
 * A forma do coroamento a partir de três vizinhos: o de cima e as duas quinas de cima.
 *
 * Recebe "é massa de rocha?" já resolvido pelo chamador porque quem sabe ler o terreno é o
 * `Terrain`, e não a arte — o mesmo contrato que `masonry` já tinha.
 */
export function crownOf(above: boolean, northWest: boolean, northEast: boolean): Crown {
  if (above) return Crown.None;
  if (northWest && northEast) return Crown.Notch;
  if (northWest) return Crown.RisesWest;
  if (northEast) return Crown.RisesEast;
  return Crown.Flat;
}

/**
 * Altura da faixa de topo na coluna `x`, em pixels. Zero quando não há topo à vista ali.
 *
 * A rampa é curta — a faixa some em pouco mais de meio tile — porque ela precisa **encostar**
 * na faixa do degrau vizinho: uma rampa longa deixaria um vão escuro entre os dois e o degrau
 * voltaria a se ler como degrau, só que com a quina lixada.
 */
function crownHeight(shape: Crown, x: number, variant: number, top: number, salt: number): number {
  if (shape === Crown.None) return 0;

  const t = (x + 0.5) / TILE_SIZE;
  const ramp =
    shape === Crown.RisesWest
      ? Math.min(1, t / 0.55)
      : shape === Crown.RisesEast
        ? Math.min(1, (1 - t) / 0.55)
        : shape === Crown.Notch
          ? Math.min(1, t / 0.5, (1 - t) / 0.5)
          : 1;

  // O recorte irregular da borda de baixo, que já existia: reta, ela vira a mesma listra
  // que este bloco inteiro existe para não desenhar.
  const jag = Math.round(hash01(x, variant, salt) * 2);
  return Math.round((top + jag) * ramp);
}

/**
 * Pinta o coroamento coluna a coluna.
 *
 * Coluna a coluna, e não em três retângulos: a altura da faixa varia com `x` em toda forma
 * que não seja a reta, e é essa variação que faz a crista descer junto com a rocha.
 *
 * As duas tintas entram por parâmetro porque alvenaria e afloramento deixaram de compartilhar
 * paleta (ver `masonryBase` em `palette.ts`): o topo de um muro de cidade não pode ser o mesmo
 * do topo de uma encosta, ou o muro volta a ter o valor do calçamento em volta dele.
 */
function paintCrown(
  ctx: CanvasRenderingContext2D,
  shape: Crown,
  variant: number,
  top: number,
  salt: number,
  face: string,
  light: string,
): void {
  if (shape === Crown.None) return;

  for (let x = 0; x < TILE_SIZE; x++) {
    const height = crownHeight(shape, x, variant, top, salt);
    if (height <= 0) continue;

    px(ctx, x, 0, 1, height, face);
    px(ctx, x, 0, 1, Math.min(2, height), light);
    px(ctx, x, height - 1, 1, 1, celShadow(face, 0.28));
  }
}

/**
 * **Superfície**: o piso que de fato é desenhado no tile.
 *
 * ## Por que ela não é o material
 *
 * `Material` é a lista do que **mistura** com o quê, e três pisos diferentes compartilham
 * `Pavement` de propósito: a laje de dentro da casa, o paralelepípedo da rua e o piso
 * consagrado da praça fazem a mesma borda com a grama, e um material por piso custaria 256
 * quadros de transição cada um para produzir uma divisa idêntica.
 *
 * O preço disso só aparecia no **chão deduzido**. Árvore, moita, lampião, placa, engradado —
 * tudo que é peça sobre o solo — não traz o próprio piso no byte do tile: ele vem da
 * vizinhança (ver `Terrain`). E a vizinhança respondia em material, então a resposta para um
 * lampião plantado no meio da rua de paralelepípedo era `Pavement`, que desenha **laje**. O
 * que se via em jogo era um tapete de laje clara sob cada poste, cada árvore e cada placa da
 * cidade, recortado na grade — a queixa de que "as luminárias, árvores e arbustos têm o chão
 * diferente", literal: eles tinham mesmo.
 *
 * Duas listas, então. A superfície diz o que desenhar e é o que a vizinhança devolve; o
 * material continua dizendo o que mistura com o quê, e sai da superfície por `SURFACE_MATERIAL`.
 * Nenhum quadro novo de transição, e o poste passa a ficar em pé sobre a rua em que está.
 */
export const Surface = {
  ...Material,
  Sanctuary: 7,
  Bones: 8,
  Cobble: 9,
} as const;

export type Surface = (typeof Surface)[keyof typeof Surface];

export const SURFACE_COUNT = 10;

/**
 * O material de mistura de cada superfície. Os sete primeiros são o próprio material —
 * `Surface` estende `Material` sem renumerar, e é isso que mantém a conta trivial.
 */
const SURFACE_MATERIAL: readonly Material[] = [
  Material.Grass,
  Material.Dirt,
  Material.Cave,
  Material.Sand,
  Material.Pavement,
  Material.Water,
  Material.Lava,
  Material.Pavement, // Sanctuary: o piso é outro, a divisa com a grama é a do calçamento.
  Material.Cave, // Ossada: rocha de caverna com ossos por cima.
  Material.Pavement, // Calçada de pedra: mesma divisa, outro desenho.
];

/** O bloco do tileset que cada superfície desenha. */
const SURFACE_BLOCK: readonly number[] = [
  GroundBlock.Grass,
  GroundBlock.Dirt,
  GroundBlock.Cave,
  GroundBlock.Sand,
  GroundBlock.Pavement,
  GroundBlock.Water,
  GroundBlock.Lava,
  GroundBlock.Sanctuary,
  GroundBlock.Bones,
  GroundBlock.Cobble,
];

export function materialOfSurface(surface: Surface): Material {
  return SURFACE_MATERIAL[surface] ?? Material.Grass;
}

/**
 * Superfície de um tipo de tile, ou `null` para o que não é solo.
 *
 * Parede devolve `null` porque ela não participa da mistura: uma parede não transborda
 * sobre a grama nem recebe borda de terra — ela projeta sombra, que é outra camada.
 * Árvore, pedra e moita também, porque o solo debaixo delas é **deduzido** da vizinhança
 * (ver `Terrain`), e não vem no byte do tile.
 */
export function surfaceOf(tile: number): Surface | null {
  switch (tile) {
    case TileType.Floor:
      return Surface.Grass;
    case TileType.Dirt:
      return Surface.Dirt;
    case TileType.Sand:
      return Surface.Sand;
    case TileType.Pavement:
    case TileType.StairsUp:
    case TileType.StairsDown:
    case TileType.Portal:
    // Os mecanismos assentam sobre piso trabalhado: porta, alavanca e baú são objetos de
    // construção, e a borda que eles fazem com a grama é a mesma do calçamento em volta.
    case TileType.DoorClosed:
    case TileType.DoorOpen:
    case TileType.DoorLocked:
    case TileType.Lever:
    case TileType.Chest:
      return Surface.Pavement;
    // A zona de proteção e a calçada da rua misturam como calçamento — o desenho é outro, a
    // divisa com a grama é a mesma —, mas cada uma tem a **sua** superfície: é ela que o chão
    // deduzido de um poste ou de uma árvore plantada em cima delas vai desenhar.
    case TileType.Sanctuary:
      return Surface.Sanctuary;
    case TileType.Cobble:
      return Surface.Cobble;
    case TileType.CaveFloor:
      return Surface.Cave;
    case TileType.Bones:
      return Surface.Bones;
    // O buraco é o chão da caverna com uma coisa nele.
    case TileType.Hole:
      return Surface.Cave;
    case TileType.Water:
    // O barco flutua: o chão debaixo dele é a mesma água do resto da enseada, e é ela que
    // faz a borda com a areia da praia. Devolver `null` aqui abriria um buraco de material
    // no meio do mar, e a orla se desenharia em volta do casco como se ele fosse ilha.
    case TileType.Boat:
      return Surface.Water;
    case TileType.Lava:
      return Surface.Lava;
    default:
      return null;
  }
}

/** Material de um tipo de tile, ou `null` para o que não é solo. */
export function materialOf(tile: number): Material | null {
  const surface = surfaceOf(tile);
  return surface === null ? null : materialOfSurface(surface);
}

/**
 * Quadro do solo. Escada, portal e parede têm bloco próprio; o resto é material.
 *
 * `masonry` separa alvenaria de rocha bruta. É a mesma `TileType.Wall` nos dois casos —
 * o servidor não tem por que distinguir, porque para a colisão são idênticas —, mas
 * desenhá-las igual faz a encosta da montanha e a parede da caverna aparecerem como
 * tijolo assentado, que é a coisa mais fora de lugar que um cenário natural pode ter.
 *
 * `crown` diz que forma tem o topo à vista (ver `Crown`) e `foot` diz se este é o tile mais
 * baixo da massa — o único que leva a sombra de contato com o chão. Quem decide os três é a
 * vizinhança, em `Terrain`.
 *
 * `fallback` é a **superfície** deduzida da vizinhança, e não o material: é ela que vale para
 * o tile que não traz piso próprio — a árvore, o poste, o engradado —, e é por ser superfície
 * que um poste no meio da rua fica sobre paralelepípedo em vez de sobre laje. Ver `Surface`.
 */
export function groundFrame(
  tile: number,
  variant: number,
  fallback: Surface,
  masonry = true,
  crown: Crown = Crown.Flat,
  foot = true,
): number {
  const face = faceOffset(crown, foot);
  const wall = (masonry ? GroundBlock.Wall : GroundBlock.Cliff) + face;

  const block =
    tile === TileType.Wall
      ? wall
      : tile === TileType.OreVein
        ? GroundBlock.Ore + face
        : (SPECIAL_BLOCKS[tile] ??
          materialOf(tile) ??
          SURFACE_BLOCK[fallback] ??
          GroundBlock.Grass);

  return block * VARIANTS + (variant % VARIANTS);
}

/**
 * Tiles com bloco próprio, fora da família de parede.
 *
 * Tabela, e não a escada de ternários que havia: com quatro tipos era legível, e com treze
 * viraria uma expressão de trinta linhas em que cada tipo novo entra no meio de uma
 * indentação que ninguém revisa.
 */
const SPECIAL_BLOCKS: Readonly<Record<number, number>> = {
  [TileType.StairsUp]: GroundBlock.StairsUp,
  [TileType.StairsDown]: GroundBlock.StairsDown,
  [TileType.Portal]: GroundBlock.Portal,
  [TileType.Sanctuary]: GroundBlock.Sanctuary,
  [TileType.Bones]: GroundBlock.Bones,
  [TileType.DoorClosed]: GroundBlock.DoorClosed,
  [TileType.DoorOpen]: GroundBlock.DoorOpen,
  [TileType.DoorLocked]: GroundBlock.DoorLocked,
  [TileType.Hole]: GroundBlock.Hole,
  [TileType.Lever]: GroundBlock.Lever,
  [TileType.Chest]: GroundBlock.Chest,
  [TileType.Cobble]: GroundBlock.Cobble,
};

/** Quadro da borda de `material` com a vizinhança `mask` (8 bits, N no menos significativo). */
export function edgeFrame(material: Material, mask: number): number {
  return material * 256 + mask;
}

/** Quinas da máscara de corte, na ordem de `makeCornerPiece`: NE, SE, SO, NO. */
export const Cut = {
  NorthEast: 1,
  SouthEast: 2,
  SouthWest: 4,
  NorthWest: 8,
} as const;

const CUT_SHAPES = 16;

/**
 * Quadro da **quina cortada** (PRD §15.8): `material` mordendo as quinas convexas do tile.
 *
 * ## O que ele conserta
 *
 * A camada de bordas só sabe fazer o material **mais forte** transbordar sobre o mais fraco:
 * a areia invade a grama, a água molha a areia. É a metade certa do problema e resolve o lado
 * de fora da margem — o tile de grama ao lado do lago recebe uma faixa de água com espuma, e
 * aquela faixa é irregular.
 *
 * A outra metade nunca foi desenhada, e é a que se vê na tela: **o material forte continua
 * sendo um quadrado**. A água do lago ocupa a célula inteira, de canto a canto, então a
 * silhueta do lago é literalmente a grade — uma poça de um tile é um quadrado azul de 32 px,
 * e uma de cinco tiles em cruz é uma cruz de quadrados. Nenhuma quantidade de espuma na borda
 * conserta isso, porque a espuma está do lado de fora de uma quina que continua a 90°.
 *
 * Aqui a quina é **cortada**: onde a diagonal e os dois lados vizinhos dela são todos de um
 * material mais fraco, aquele material entra por cima da quina no quarto de disco de
 * `makeCornerPiece`. A poça de um tile vira redonda, a cruz vira trevo, e a margem de um lago
 * grande deixa de ter oito ângulos retos por curva.
 *
 * ## Por que o mesmo quarto de disco da peça de canto
 *
 * É a mesma junta vista dos dois lados. A peça de canto preenche a diagonal em que dois
 * materiais só se tocam pela quina; o corte tira a quina que sobra do outro lado da mesma
 * diagonal. Desenhá-los com raios diferentes faria a margem parecer costurada com duas
 * agulhas — e reaproveitar a peça custa zero pixel a mais de textura.
 */
export function cutFrame(material: Material, cuts: number): number {
  return MATERIAL_COUNT * 256 + material * CUT_SHAPES + (cuts & 15);
}

/** Variação estável de um tile. Hash inteiro barato, sem RNG e sem estado. */
export function tileVariant(x: number, y: number): number {
  const hash = (x * 73_856_093) ^ (y * 19_349_663);
  return Math.abs(hash) % VARIANTS;
}

// --- Vegetação ---------------------------------------------------------------

export const Prop = {
  Tree: 0,
  Rock: 1,
  Bush: 2,
  /** O subsolo (PRD §15.6): quatro peças que só existem debaixo da terra. */
  Stalagmite: 3,
  Crystal: 4,
  Mushroom: 5,
  Web: 6,
  /**
   * O barco do cais (PRD §15.8).
   *
   * Entra como peça e não como material de chão pelo mesmo motivo da árvore: ele é um
   * objeto **sobre** a água, e o mastro precisa transbordar para a célula de trás — um
   * barco que coubesse em 32 px seria um bote de brinquedo, e "barco médio" é o que o cais
   * promete.
   */
  Boat: 7,

  /**
   * A mobília da cidade (PRD §15.8).
   *
   * Os cinco entram como **peça** e não como material de chão pelo mesmo motivo da árvore e
   * do barco: os cinco têm altura, e altura precisa transbordar para a célula de trás. O
   * lampião é o caso extremo — dois terços dele moram no tile de cima, que é exatamente o
   * que faz um poste ler como poste em vez de como tampa de bueiro.
   *
   * O chão debaixo deles vem da vizinhança (ver `materialAt`), e é por isso que o mesmo
   * balcão serve a uma loja de calçamento e a um cais de tábua sem uma segunda arte.
   */
  Counter: 8,
  Fence: 9,
  Crate: 10,
  Lamp: 11,
  Sign: 12,
} as const;

export type Prop = (typeof Prop)[keyof typeof Prop];

const PROP_COUNT = 13;

const PROPS_BY_TILE: Readonly<Record<number, Prop>> = {
  [TileType.Tree]: Prop.Tree,
  [TileType.Rock]: Prop.Rock,
  [TileType.Bush]: Prop.Bush,
  [TileType.Stalagmite]: Prop.Stalagmite,
  [TileType.Crystal]: Prop.Crystal,
  [TileType.Mushroom]: Prop.Mushroom,
  [TileType.Web]: Prop.Web,
  [TileType.Boat]: Prop.Boat,
  [TileType.Counter]: Prop.Counter,
  [TileType.Fence]: Prop.Fence,
  [TileType.Crate]: Prop.Crate,
  [TileType.Lamp]: Prop.Lamp,
  [TileType.Sign]: Prop.Sign,
};

export function propOf(tile: number): Prop | null {
  return PROPS_BY_TILE[tile] ?? null;
}

/** Quadro da metade de baixo (`half` 0) ou de cima (`half` 1) de um obstáculo. */
export function propFrame(prop: Prop, variant: number, half: 0 | 1): number {
  return (prop * 2 + half) * VARIANTS + (variant % VARIANTS);
}

// --- Sombra ------------------------------------------------------------------

/**
 * A sombra tem só três bits — norte, oeste e a quina noroeste — porque a luz do jogo vem
 * de cima e da esquerda. Sombra dos quatro lados não seria sombra: seria contorno, e
 * contorno já é trabalho do desenho de cada peça.
 */
export const SHADE_N = 1;
export const SHADE_W = 2;
export const SHADE_NW = 4;
const SHADE_FRAMES = 8;

// --- Geração -----------------------------------------------------------------

/**
 * Hash → [0, 1). É o "acaso" de toda a textura: determinístico para o mesmo pixel
 * sair igual em toda sessão, e barato o bastante para rodar por pixel na geração.
 */
function hash01(x: number, y: number, salt: number): number {
  let h = Math.imul(x, 374_761_393) + Math.imul(y, 668_265_263) + Math.imul(salt, 1_274_126_177);
  h = Math.imul(h ^ (h >>> 13), 1_103_515_245);
  h ^= h >>> 16;
  return (h >>> 0) / 4_294_967_296;
}

function range(count: number): number[] {
  return Array.from({ length: count }, (_, i) => i);
}

interface TilesetAnim {
  ctx: CanvasRenderingContext2D;
  waterBases: readonly HTMLCanvasElement[];
  portalGrounds: readonly HTMLCanvasElement[];
  lastStep: number;
}

const animations = new WeakMap<Phaser.Scene, TilesetAnim>();

export function buildTileset(scene: Phaser.Scene): void {
  if (animations.has(scene) && scene.textures.exists(TILESET_TEXTURE)) return;

  let ctx: CanvasRenderingContext2D;

  if (scene.textures.exists(TILESET_TEXTURE)) {
    // As texturas sobrevivem à cena (o TextureManager é do jogo); só o estado de
    // animação precisa renascer para a cena nova.
    const texture = scene.textures.get(TILESET_TEXTURE);
    if (!(texture instanceof Phaser.Textures.CanvasTexture)) return;
    ctx = texture.context;
  } else {
    const canvas = createCanvas(TILE_SIZE * VARIANTS, TILE_SIZE * GROUND_BLOCKS);
    ctx = context2d(canvas);

    for (let variant = 0; variant < VARIANTS; variant++) {
      paint(ctx, variant, Material.Grass, drawGrass);
      paint(ctx, variant, Material.Dirt, drawDirt);
      paint(ctx, variant, Material.Sand, drawSand);
      paint(ctx, variant, Material.Pavement, drawPavement);
      paint(ctx, variant, Material.Cave, drawCaveFloor);
      paint(ctx, variant, GroundBlock.StairsUp, (c) => drawStairs(c, true));
      paint(ctx, variant, GroundBlock.StairsDown, (c) => drawStairs(c, false));
      for (let shape = 0; shape < CROWN_SHAPES; shape++) {
        const crown = shape as Crown;

        for (const foot of [false, true]) {
          const offset = faceOffset(crown, foot);
          paint(ctx, variant, GroundBlock.Wall + offset, (c, v) => drawWall(c, v, crown, foot));
          paint(ctx, variant, GroundBlock.Cliff + offset, (c, v) => drawCliff(c, v, crown, foot));
          paint(ctx, variant, GroundBlock.Ore + offset, (c, v) => drawOre(c, v, crown, foot));
        }
      }
      paint(ctx, variant, GroundBlock.Sanctuary, drawSanctuary);
      paint(ctx, variant, GroundBlock.Bones, drawBoneFloor);

      // Os mecanismos, todos sobre o piso a que pertencem: a porta sobre calçamento, o
      // buraco sobre rocha de caverna. Desenhar o piso aqui dentro — e não contar com a
      // camada de baixo — é o que faz a peça ler como peça e não como decalque.
      paint(ctx, variant, GroundBlock.DoorClosed, (c, v) => drawDoor(c, v, 'closed'));
      paint(ctx, variant, GroundBlock.DoorOpen, (c, v) => drawDoor(c, v, 'open'));
      paint(ctx, variant, GroundBlock.DoorLocked, (c, v) => drawDoor(c, v, 'locked'));
      paint(ctx, variant, GroundBlock.Hole, drawHole);
      paint(ctx, variant, GroundBlock.Lever, drawLever);
      paint(ctx, variant, GroundBlock.Chest, drawChest);
      paint(ctx, variant, GroundBlock.Cobble, drawCobble);
    }

    addGridTexture(scene, TILESET_TEXTURE, canvas, TILE_SIZE, TILE_SIZE, VARIANTS, GROUND_BLOCKS);
  }

  // Fora do ramo acima: cada uma tem a própria guarda de existência, e deixá-las
  // amarradas à criação do solo faria uma textura perdida no meio do caminho nunca mais
  // ser regerada.
  buildEdges(scene);
  buildShade(scene);
  buildProps(scene);

  const anim: TilesetAnim = {
    ctx,
    waterBases: range(VARIANTS).map(makeWaterBase),
    portalGrounds: range(VARIANTS).map(makePortalGround),
    lastStep: 0,
  };

  drawAnimatedRows(anim, 0);
  animations.set(scene, anim);

  // As linhas animadas foram desenhadas depois de a textura subir; sem o refresh, a
  // água ficaria em branco até a primeira troca de fase.
  const texture = scene.textures.get(TILESET_TEXTURE);
  if (texture instanceof Phaser.Textures.CanvasTexture) texture.refresh();
}

/**
 * Avança a animação do tileset. Chamada a cada quadro pela cena; só faz trabalho quando
 * a fase discreta muda (a cada `PHASE_MS`), então o custo por quadro é uma comparação.
 */
export function animateTileset(scene: Phaser.Scene, timeMs: number): void {
  const anim = animations.get(scene);
  if (!anim) return;

  const step = Math.floor(timeMs / PHASE_MS) % PHASES;
  if (step === anim.lastStep) return;
  anim.lastStep = step;

  drawAnimatedRows(anim, step / PHASES);

  const texture = scene.textures.get(TILESET_TEXTURE);
  if (texture instanceof Phaser.Textures.CanvasTexture) texture.refresh();
}

function drawAnimatedRows(anim: TilesetAnim, phase: number): void {
  for (let variant = 0; variant < VARIANTS; variant++) {
    paint(anim.ctx, variant, Material.Water, (c, v) => paintWater(c, anim, v, phase));
    // A lava anima pelo mesmo relógio da água, e é o que a faz ler como líquido: crosta
    // parada com veio quente correndo por baixo. Sem movimento ela vira tapete laranja.
    paint(anim.ctx, variant, Material.Lava, (c, v) => paintLava(c, v, phase));
    paint(anim.ctx, variant, GroundBlock.Portal, (c, v) => {
      const ground = anim.portalGrounds[v];
      if (ground) c.drawImage(ground, 0, 0);
      paintPortalRings(c, phase);
    });
  }
}

function paint(
  ctx: CanvasRenderingContext2D,
  variant: number,
  block: number,
  draw: (ctx: CanvasRenderingContext2D, variant: number) => void,
): void {
  ctx.save();
  ctx.translate(variant * TILE_SIZE, block * TILE_SIZE);
  // Recorte no quadro: um desenho que vazasse — a crista de onda na beira do tile, por
  // exemplo — contaminaria o tile vizinho dentro do mesmo tileset.
  ctx.beginPath();
  ctx.rect(0, 0, TILE_SIZE, TILE_SIZE);
  ctx.clip();
  ctx.clearRect(0, 0, TILE_SIZE, TILE_SIZE);
  draw(ctx, variant);
  ctx.restore();
}

/**
 * Fundo de qualquer material: ruído em células de 2×2 dentro de uma rampa curta.
 *
 * A média de dois hashes concentra o resultado nos tons do meio da rampa, então o campo
 * lê como uma cor só com respiração — e não como confete, que é o que uma rampa larga
 * sorteada uniformemente produz.
 */
function fillRamp(
  ctx: CanvasRenderingContext2D,
  ramp: readonly string[],
  variant: number,
  salt: number,
): void {
  for (let y = 0; y < TILE_SIZE; y += 2) {
    for (let x = 0; x < TILE_SIZE; x += 2) {
      const mix = (hash01(x, y, salt + variant) + hash01(x, y, salt + 40 + variant)) / 2;
      const tone = ramp[Math.min(ramp.length - 1, Math.floor(mix * ramp.length))];
      if (tone) px(ctx, x, y, 2, 2, tone);
    }
  }
}

// --- Grama -------------------------------------------------------------------

function drawGrass(ctx: CanvasRenderingContext2D, variant: number): void {
  fillRamp(ctx, TILE_PALETTE.grassRamp, variant, 11);

  // Manchas escuras primeiro, tufos por cima: a ordem é a profundidade. Elas são massas
  // de lóbulos, e não retângulos: o gramado de desenho japonês é feito de moitas com
  // recorte, e um retângulo escuro no meio da grama lê como falha de textura.
  for (let i = 0; i < 2; i++) {
    const cx = 4 + Math.floor(hash01(i, variant, 301) * (TILE_SIZE - 8));
    const cy = 4 + Math.floor(hash01(i, variant, 302) * (TILE_SIZE - 8));
    cloud(ctx, cx, cy, 5, 3.2, 4, variant * 7 + i, TILE_PALETTE.grassPatch);
  }

  // Uma mancha de sol por tile: a mesma grama, um degrau mais clara e mais quente. É o
  // que tira do gramado a leitura de tapete impresso — num campo iluminado, a luz não
  // chega igual em toda parte, e é a variação **larga** que diz isso. O ruído por pixel
  // do fundo dá matéria; ele não dá iluminação.
  const sx = 6 + Math.floor(hash01(3, variant, 311) * (TILE_SIZE - 12));
  const sy = 6 + Math.floor(hash01(4, variant, 312) * (TILE_SIZE - 12));
  ctx.globalAlpha = 0.3;
  cloud(ctx, sx, sy, 9, 6, 5, variant * 11 + 3,
    celLight(TILE_PALETTE.grassRamp[3] ?? '#6ba854', 0.22));
  ctx.globalAlpha = 1;

  // Tufos: três lâminas juntas, pé escuro e ponta clara. É o detalhe que o olho registra
  // como "grama" mesmo sem conseguir contar as folhas — e em tufo, não espalhado, porque
  // capim nasce em moita.
  const tufts = 2 + (variant % 2);
  for (let i = 0; i < tufts; i++) {
    const bx = 3 + Math.floor(hash01(i, variant, 101) * (TILE_SIZE - 8));
    const by = 4 + Math.floor(hash01(i, variant, 202) * (TILE_SIZE - 10));

    for (let blade = 0; blade < 3; blade++) {
      const x = bx + blade * 2;
      const height = 3 + Math.floor(hash01(i * 3 + blade, variant, 303) * 3);
      px(ctx, x, by - height, 1, height, TILE_PALETTE.grassBlade);
      px(ctx, x, by - height, 1, 2, TILE_PALETTE.grassBladeLight);
    }
  }

  // Acentos raros: com seis variações, cada acento aparece em ~1/6 dos tiles — o
  // suficiente para quebrar a monotonia sem virar tapete de flor.
  if (variant === 3) {
    drawFlower(ctx, 9 + Math.floor(hash01(0, variant, 401) * 14), 11, TILE_PALETTE.flowerPetal);
  } else if (variant === 5) {
    drawFlower(ctx, 7 + Math.floor(hash01(0, variant, 402) * 8), 21, TILE_PALETTE.flowerAlt);
    drawFlower(ctx, 19 + Math.floor(hash01(1, variant, 403) * 7), 9, TILE_PALETTE.flowerAlt);
  }
}

function drawFlower(ctx: CanvasRenderingContext2D, x: number, y: number, petal: string): void {
  px(ctx, x, y + 2, 1, 3, TILE_PALETTE.flowerLeaf);
  px(ctx, x - 1, y + 3, 2, 1, TILE_PALETTE.flowerLeaf);
  px(ctx, x - 1, y, 1, 1, petal);
  px(ctx, x + 1, y, 1, 1, petal);
  px(ctx, x, y - 1, 1, 1, petal);
  px(ctx, x, y + 1, 1, 1, petal);
  px(ctx, x, y, 1, 1, shade(petal, -0.4));
}

// --- Terra -------------------------------------------------------------------

function drawDirt(ctx: CanvasRenderingContext2D, variant: number): void {
  fillRamp(ctx, TILE_PALETTE.dirtRamp, variant, 71);

  // Uma depressão e uma elevação por tile, em massas de lóbulo. Terra batida não é plana:
  // ela tem cava e lombo, e é a variação **larga** que diz isso. O ruído por pixel do
  // fundo dá grão, e grão não é relevo — sem estas duas manchas, uma estrada de vinte
  // tiles lê como papel pardo.
  const patch = (salt: number, color: string, alpha: number): void => {
    ctx.globalAlpha = alpha;
    cloud(
      ctx,
      5 + Math.floor(hash01(0, variant, salt) * (TILE_SIZE - 10)),
      5 + Math.floor(hash01(1, variant, salt + 1) * (TILE_SIZE - 10)),
      8,
      5.5,
      4,
      variant * 5 + salt,
      color,
    );
    ctx.globalAlpha = 1;
  };
  patch(731, TILE_PALETTE.dirtPatch, 0.5);
  patch(737, celLight(TILE_PALETTE.dirtRamp[3] ?? '#9a7c59', 0.2), 0.42);

  // Sulcos: traços horizontais escuros de comprimento variável. Terra batida tem a marca
  // do que passou por cima, e é essa direção — horizontal, contra a vertical da grama —
  // que faz os dois materiais se distinguirem mesmo em tons parecidos.
  for (let i = 0; i < 4; i++) {
    const x = Math.floor(hash01(i, variant, 501) * (TILE_SIZE - 10));
    const y = Math.floor(hash01(i, variant, 502) * TILE_SIZE);
    const width = 4 + Math.floor(hash01(i, variant, 503) * 7);
    px(ctx, x, y, width, 1, TILE_PALETTE.dirtPatch);
  }

  // Pedriscos.
  const stones = variant % 3;
  for (let i = 0; i <= stones; i++) {
    const x = 2 + Math.floor(hash01(i, variant, 601) * (TILE_SIZE - 6));
    const y = 2 + Math.floor(hash01(i, variant, 602) * (TILE_SIZE - 5));
    px(ctx, x, y, 3, 2, TILE_PALETTE.dirtStone);
    px(ctx, x, y + 2, 3, 1, TILE_PALETTE.dirtStoneShade);
  }
}

// --- Areia -------------------------------------------------------------------

function drawSand(ctx: CanvasRenderingContext2D, variant: number): void {
  fillRamp(ctx, TILE_PALETTE.sandRamp, variant, 131);

  // Grão fino: pixels isolados, e não blocos de 2×2. Areia é o único material em que o
  // ruído de um pixel é o desenho certo — é literalmente do tamanho do grão.
  for (let y = 0; y < TILE_SIZE; y++) {
    for (let x = 0; x < TILE_SIZE; x++) {
      if (hash01(x, y, 701 + variant) > 0.93) px(ctx, x, y, 1, 1, TILE_PALETTE.sandGrain);
    }
  }

  if (variant % 3 === 0) {
    const x = 6 + Math.floor(hash01(0, variant, 801) * 18);
    const y = 6 + Math.floor(hash01(1, variant, 802) * 18);
    px(ctx, x, y, 3, 2, TILE_PALETTE.sandShell);
    px(ctx, x + 1, y - 1, 1, 1, TILE_PALETTE.sandShell);
  }
}

// --- Calçamento --------------------------------------------------------------

/**
 * Lajota, e não bloco liso: a junta é o que diz "isto é obra".
 *
 * ## Por que as lajotas têm tamanhos diferentes
 *
 * A versão anterior punha quatro lajotas de dezesseis por dezesseis por tile, sempre nas
 * mesmas posições, e alternava o deslocamento entre variações. Dava um xadrez: com só
 * duas posições possíveis, uma praça de vinte tiles mostra o mesmo par se repetindo, e o
 * olho fecha o padrão em menos de um segundo. Somava-se a isso a junta desenhada como
 * linha reta de ponta a ponta, que reforçava a mesma grade.
 *
 * Aqui cada lajota sorteia largura e altura dentro de uma faixa, a fiada seguinte começa
 * onde a anterior terminou, e a junta é o **vão** entre elas — o fundo escuro aparecendo,
 * que é o que uma junta de calçada é. Sem tamanho fixo não há padrão para o olho fechar.
 */
function drawPavement(ctx: CanvasRenderingContext2D, variant: number): void {
  px(ctx, 0, 0, TILE_SIZE, TILE_SIZE, TILE_PALETTE.paveJoint);

  let y = -3 - Math.floor(hash01(0, variant, 903) * 6);
  let row = 0;

  while (y < TILE_SIZE) {
    const height = 11 + Math.floor(hash01(row, variant, 905) * 6);
    let x = -4 - Math.floor(hash01(row, variant, 907) * 10);

    while (x < TILE_SIZE) {
      const width = 11 + Math.floor(hash01(x + 70, row, 901 + variant) * 8);
      const roll = hash01(x + 13, row * 5, 909 + variant);
      const tone =
        TILE_PALETTE.paveRamp[Math.min(3, Math.floor(roll * TILE_PALETTE.paveRamp.length))] ??
        TILE_PALETTE.paveRamp[1]!;

      roundRect(ctx, x, y, width - 2, height - 2, 1.5, tone);
      // Quina iluminada em cima e à esquerda, sombra embaixo: uma lajota plana lê como
      // buraco, e a luz do jogo inteiro vem do noroeste.
      roundRect(ctx, x, y, width - 2, 1.2, 0.6, celLight(tone, 0.26));
      roundRect(ctx, x, y, 1.2, height - 2, 0.6, celLight(tone, 0.16));
      roundRect(ctx, x, y + height - 3.2, width - 2, 1.2, 0.6, celShadow(tone, 0.24));

      // Desgaste: alguns pixels mais escuros por lajota, sempre nos mesmos lugares.
      for (let i = 0; i < 3; i++) {
        const wx = x + 2 + Math.floor(hash01(i, x + row * 7, 921 + variant) * (width - 6));
        const wy = y + 2 + Math.floor(hash01(i, x + row * 3, 923 + variant) * (height - 6));
        px(ctx, wx, wy, 2, 1, celShadow(tone, 0.12));
      }

      x += width;
    }

    y += height;
    row++;
  }

  // Trinca ocasional: pedra sem defeito parece plástico.
  if (variant % 4 === 1) {
    const x = 6 + Math.floor(hash01(0, variant, 911) * 18);
    for (let i = 0; i < 7; i++) {
      px(ctx, x + Math.round(Math.sin(i) * 1.5), 8 + i, 1, 1, TILE_PALETTE.paveCrack);
    }
  }
}

// --- Calçada de pedra --------------------------------------------------------

/**
 * A rua da cidade: seixo miúdo em fiadas, assentado em rejunte escuro (PRD §15.8).
 *
 * ## O que a distingue da laje de dentro de casa
 *
 * Três coisas, e as três são de propósito. A pedra é **pequena** — cinco por fiada contra
 * duas lajotas por tile —, então a rua tem seis vezes mais junta por metro que o interior, e
 * junta é o que o olho lê como textura de via. O tom é **um degrau mais escuro**, o que faz a
 * soleira iluminada de cada porta se destacar do calçamento em vez de se dissolver nele. E as
 * fiadas são **horizontais e regulares**, contra a fiada corrida e desalinhada da laje: uma
 * rua precisa ter direção, porque é por ela que se lê para onde a via vai.
 *
 * ## Fiada em arco, não em xadrez
 *
 * Cada fiada é deslocada meia pedra em relação à de cima — assentamento de meia-vez, que é
 * como calçada de paralelepípedo é feita —, e a largura de cada seixo varia dentro de uma
 * faixa estreita. Sem o deslocamento, quatro tiles de rua lado a lado mostram uma coluna de
 * junta atravessando a tela inteira, e o olho fecha a grade antes de ler a pedra: é o mesmo
 * defeito que `drawPavement` já teve de consertar uma vez, e a correção aqui é a mesma.
 */
function drawCobble(ctx: CanvasRenderingContext2D, variant: number): void {
  px(ctx, 0, 0, TILE_SIZE, TILE_SIZE, TILE_PALETTE.cobbleJoint);

  /** Altura da fiada. Seis dá cinco fiadas e meia por tile, que é seixo de rua. */
  const course = 6;

  for (let row = -1; row * course < TILE_SIZE; row++) {
    const y = row * course;
    // Meia-vez: fiada par começa alinhada, ímpar entra meia pedra. O deslocamento é o que
    // impede a junta de formar coluna entre um tile e o de baixo.
    let x = row % 2 === 0 ? -1 : -4;

    while (x < TILE_SIZE) {
      const width = 5 + Math.floor(hash01(x + row * 11, variant, 1301) * 3);
      const roll = hash01(x * 3, row + variant * 7, 1303);
      const tone =
        TILE_PALETTE.cobbleRamp[
          Math.min(TILE_PALETTE.cobbleRamp.length - 1, Math.floor(roll * TILE_PALETTE.cobbleRamp.length))
        ] ?? TILE_PALETTE.cobbleRamp[1]!;

      // Seixo arredondado, com a quina noroeste iluminada e o pé na sombra. A mesma luz do
      // resto do cenário: um seixo plano lê como azulejo.
      roundRect(ctx, x, y, width - 1, course - 1, 1.8, tone);
      roundRect(ctx, x, y, width - 1, 1, 0.5, celLight(tone, 0.3));
      roundRect(ctx, x, y + course - 2, width - 1, 1, 0.5, celShadow(tone, 0.26));

      x += width;
    }
  }

  // Poça e areia acumulada entre as pedras, uma variação em três: rua sem sujeira lê como
  // maquete. A poça é escura e opaca, e não azul — água de chuva em pedra é sombra molhada.
  if (variant % 3 === 1) {
    const cx = 8 + ((variant * 7) % 16);
    ctx.globalAlpha = 0.3;
    ellipse(ctx, cx, TILE_SIZE / 2 + 4, 6, 3, TILE_PALETTE.cobbleJoint);
    ctx.globalAlpha = 1;
    ellipse(ctx, cx - 1, TILE_SIZE / 2 + 3, 2, 1, TILE_PALETTE.cobbleLight);
  }
}

// --- Piso consagrado ---------------------------------------------------------

/**
 * Zona de proteção: o mesmo calçamento, com uma malha de veios claros por cima.
 *
 * A fronteira da zona precisa ser **visível de relance e sem legenda** — é ela que separa
 * "estou a salvo" de "estou apanhando", e um jogador que só descobre onde ela acaba ao
 * morrer do lado de fora não ganhou proteção nenhuma. Ao mesmo tempo, ela não pode ser um
 * bloco de cor chapada: a praça é o lugar em que o jogador passa o primeiro minuto e vai
 * revisitar a cada morte, e uma laje azul ali cansa antes do terceiro retorno.
 *
 * A solução é partir do calçamento — mesmo material, mesma junta, mesma borda com a grama
 * (ver `materialOf`) — e acrescentar duas coisas: uma malha diagonal de veios frios, que
 * é o que se lê como "isto foi consagrado", e um clareamento leve por cima, que separa a
 * praça do calçamento comum das casas mesmo com a malha fora de foco.
 */
function drawSanctuary(ctx: CanvasRenderingContext2D, variant: number): void {
  drawPavement(ctx, variant);

  // Clareamento: o piso da zona é o mais claro do mapa, e é essa diferença de valor — não
  // a de matiz — que sobrevive a um zoom de 1×.
  ctx.globalAlpha = 0.14;
  px(ctx, 0, 0, TILE_SIZE, TILE_SIZE, '#cfe3f5');
  ctx.globalAlpha = 1;

  // Veios em duas diagonais opostas: uma malha só, na mesma inclinação, leria como
  // ripado de madeira. Cruzadas, leem como incrustação.
  ctx.globalAlpha = 0.5;
  for (let i = -TILE_SIZE; i < TILE_SIZE * 2; i += 8) {
    for (let step = 0; step < TILE_SIZE; step++) {
      px(ctx, i + step, step, 1, 1, TILE_PALETTE.sanctuaryVein);
      px(ctx, i + step, TILE_SIZE - 1 - step, 1, 1, TILE_PALETTE.sanctuaryVein);
    }
  }
  ctx.globalAlpha = 1;

  // Um nó de luz nos cruzamentos da malha, uma variação sim outra não: sem ele o
  // reticulado fica mecânico, e o que se quer é pedra trabalhada, não papel milimetrado.
  // Em meia opacidade: com o piso já clareado, um ponto branco cheio a cada dois tiles
  // vira sarampo — o nó tem que se ler como veio grosso, não como estrela.
  if (variant % 2 === 0) {
    const cx = 8 + ((variant * 5) % 16);
    ctx.globalAlpha = 0.55;
    ellipse(ctx, cx, TILE_SIZE / 2, 2.5, 2.5, TILE_PALETTE.sanctuaryGlow);
    ellipse(ctx, cx, TILE_SIZE / 2, 1.2, 1.2, '#ffffff');
    ctx.globalAlpha = 1;
  }
}

// --- Rocha do subsolo --------------------------------------------------------

function drawCaveFloor(ctx: CanvasRenderingContext2D, variant: number): void {
  fillRamp(ctx, TILE_PALETTE.caveRamp, variant, 161);

  for (let i = 0; i < 3; i++) {
    const x = Math.floor(hash01(i, variant, 1001) * (TILE_SIZE - 8));
    const y = Math.floor(hash01(i, variant, 1002) * (TILE_SIZE - 4));
    const length = 4 + Math.floor(hash01(i, variant, 1003) * 8);

    for (let step = 0; step < length; step++) {
      px(ctx, x + step, y + Math.round(Math.sin(step * 0.9) * 1.2), 1, 1, TILE_PALETTE.caveCrack);
    }
  }

  for (let i = 0; i < 6; i++) {
    const x = Math.floor(hash01(i, variant, 1101) * TILE_SIZE);
    const y = Math.floor(hash01(i, variant, 1102) * TILE_SIZE);
    px(ctx, x, y, 1, 1, TILE_PALETTE.caveGrit);
  }
}

/**
 * Chão de ossada: a mesma rocha do subsolo, com ossos por cima.
 *
 * Ossos **soltos e pequenos**, e nunca um esqueleto inteiro: um crânio desenhado no tile
 * vira um ícone, e um piso de ícones repetidos a cada 32 px é a grade que este arquivo
 * inteiro existe para não desenhar. O que se lê como ossada é o **campo** — traços claros
 * curtos, em direções desencontradas, sobre pedra escura.
 */
function drawBoneFloor(ctx: CanvasRenderingContext2D, variant: number): void {
  drawCaveFloor(ctx, variant);

  // Uma mancha de pó de osso por baixo: sem ela os traços flutuam sobre a rocha em vez de
  // pertencerem ao chão.
  ctx.globalAlpha = 0.22;
  cloud(
    ctx,
    6 + Math.floor(hash01(0, variant, 3401) * 20),
    6 + Math.floor(hash01(1, variant, 3402) * 20),
    9,
    6,
    4,
    variant * 3,
    TILE_PALETTE.boneShade,
  );
  ctx.globalAlpha = 1;

  const pieces = 4 + (variant % 3);

  for (let i = 0; i < pieces; i++) {
    const x = 3 + Math.floor(hash01(i, variant, 3411) * (TILE_SIZE - 10));
    const y = 3 + Math.floor(hash01(i, variant, 3412) * (TILE_SIZE - 8));
    const length = 4 + Math.floor(hash01(i, variant, 3413) * 6);
    const vertical = hash01(i, variant, 3414) < 0.42;

    // Sombra primeiro, osso por cima e deslocado para o noroeste: é o mesmo recurso de
    // volume do resto do cenário, em dois pixels.
    if (vertical) {
      px(ctx, x + 1, y + 1, 2, length, TILE_PALETTE.boneDark);
      px(ctx, x, y, 2, length, TILE_PALETTE.boneBase);
      px(ctx, x - 1, y - 1, 3, 2, TILE_PALETTE.boneBase);
      px(ctx, x - 1, y + length - 1, 3, 2, TILE_PALETTE.boneShade);
    } else {
      px(ctx, x + 1, y + 1, length, 2, TILE_PALETTE.boneDark);
      px(ctx, x, y, length, 2, TILE_PALETTE.boneBase);
      px(ctx, x - 1, y - 1, 2, 3, TILE_PALETTE.boneBase);
      px(ctx, x + length - 1, y - 1, 2, 3, TILE_PALETTE.boneShade);
    }
  }

  // Uma caveira a cada seis tiles, e nunca mais: ela é o acento que diz o que são os
  // traços em volta. Repetida em todo tile, seria papel de parede macabro.
  if (variant === 2) {
    const sx = 11 + Math.floor(hash01(0, variant, 3421) * 8);
    const sy = 13 + Math.floor(hash01(1, variant, 3422) * 6);

    ellipse(ctx, sx + 1, sy + 1, 4, 3.4, TILE_PALETTE.boneDark);
    ellipse(ctx, sx, sy, 4, 3.4, TILE_PALETTE.boneBase);
    px(ctx, sx - 2, sy - 1, 2, 2, TILE_PALETTE.boneDark);
    px(ctx, sx + 1, sy - 1, 2, 2, TILE_PALETTE.boneDark);
    px(ctx, sx - 1, sy + 2, 3, 2, TILE_PALETTE.boneShade);
  }
}

// --- Lava --------------------------------------------------------------------

/**
 * Lava: crosta escura partida, com o incandescente aparecendo nas fendas.
 *
 * A tentação é pintar o tile inteiro de laranja e pôr manchas escuras por cima. Não
 * funciona: lava a essa distância é quase toda **crosta** — o que brilha é a fresta entre as
 * placas —, e um campo laranja chapado lê como tapete, não como rocha derretida. Aqui o
 * fundo é o quente e a crosta é desenhada por cima em placas irregulares, então o que sobra
 * descoberto já é a fenda, com a forma certa e sem uma linha desenhada à mão.
 *
 * O brilho **anda** com a fase, e é o único movimento: as placas ficam paradas, porque
 * crosta parada com veio correndo por baixo é exatamente o que a lava faz.
 */
function paintLava(ctx: CanvasRenderingContext2D, variant: number, phase: number): void {
  fillRamp(
    ctx,
    [TILE_PALETTE.lavaBase, TILE_PALETTE.lavaMid, TILE_PALETTE.lavaHot, TILE_PALETTE.lavaGlow],
    variant,
    3501,
  );

  // As placas de crosta: polígonos de lados desiguais, encostados uns nos outros. O vão
  // entre eles é a fenda incandescente.
  for (let i = 0; i < 9; i++) {
    const cx = Math.floor(hash01(i, variant, 3511) * (TILE_SIZE + 10)) - 5;
    const cy = Math.floor(hash01(i, variant, 3512) * (TILE_SIZE + 10)) - 5;
    const rx = 5 + hash01(i, variant, 3513) * 6;
    const ry = 4 + hash01(i, variant, 3514) * 5;

    const plate: Shape = (c) => {
      c.beginPath();
      for (let v = 0; v < 5; v++) {
        const angle = (v / 5) * TAU + hash01(i * 5 + v, variant, 3515) * 0.8;
        const reach = 0.75 + hash01(i * 5 + v, variant, 3516) * 0.45;
        const vx = cx + Math.cos(angle) * rx * reach;
        const vy = cy + Math.sin(angle) * ry * reach;
        if (v === 0) c.moveTo(vx, vy);
        else c.lineTo(vx, vy);
      }
      c.closePath();
    };

    ctx.fillStyle = TILE_PALETTE.lavaCrust;
    plate(ctx);
    ctx.fill();

    // A quina da placa voltada para o noroeste pega o reflexo da própria fenda.
    clipped(ctx, plate, (c) => {
      c.translate(-1, -1.5);
      c.fillStyle = celLight(TILE_PALETTE.lavaCrust, 0.18);
      plate(c);
      c.fill();
    });
  }

  // Bolhas: acendem e apagam fora de fase entre si, como as cintilações da água. É o que
  // impede o tile inteiro de piscar junto — que leria como falha de render.
  for (let i = 0; i < 3; i++) {
    const bx = 3 + Math.floor(hash01(i, variant, 3521) * (TILE_SIZE - 6));
    const by = 3 + Math.floor(hash01(i, variant, 3522) * (TILE_SIZE - 6));
    const beat = (hash01(i, variant, 3523) + phase) % 1;
    if (beat > 0.35) continue;

    ctx.globalAlpha = 0.85 - beat;
    ellipse(ctx, bx, by, 2.2, 1.8, TILE_PALETTE.lavaGlow);
    ctx.globalAlpha = 1;
    px(ctx, bx, by - 1, 1, 1, '#ffffff');
  }
}

// --- Pedra construída --------------------------------------------------------

/**
 * Parede de alvenaria.
 *
 * ## O que mudou, e por quê
 *
 * A versão anterior assentava tijolos de dez por seis pixels, todos do mesmo tamanho, em
 * fiadas separadas por uma linha escura contínua de ponta a ponta do tile. O resultado
 * era papel milimetrado: o olho lia a grade de juntas antes de ler a pedra, e a linha
 * horizontal que atravessava o tile inteiro reaparecia idêntica no tile vizinho.
 *
 * Três decisões desfazem isso. As pedras têm **largura sorteada**, entre sete e catorze
 * pixels, então nenhuma junta vertical se alinha com a de outra fiada por acidente. A
 * junta horizontal deixou de ser uma linha pintada e passou a ser o **vão** entre uma
 * pedra e a próxima — o fundo escuro aparecendo por trás, que é o que uma junta é. E cada
 * pedra tem canto redondo de um pixel: pedra assentada por pedreiro tem quina gasta, e um
 * pixel de raio é a diferença entre "pedra" e "retângulo".
 *
 * O coroamento — a faixa clara do alto, que dá altura à face — só é desenhado quando
 * `crown` é verdadeiro, ou seja, quando não há parede acima. Ver `GroundBlock`.
 */
function drawWall(
  ctx: CanvasRenderingContext2D,
  variant: number,
  crown: Crown,
  foot: boolean,
): void {
  // Fundo da junta: é ele que aparece nos vãos entre as pedras, e é por isso que não há
  // linha de junta desenhada em lugar nenhum. Escuro, mas **não** o mais escuro da
  // paleta: com um pixel e meio de vão a cada onze de pedra, a junta responde por um
  // sétimo da área da parede — pintada de preto, ela puxa a muralha inteira para baixo e
  // o que se lê é uma mancha suja, não pedra assentada.
  px(ctx, 0, 0, TILE_SIZE, TILE_SIZE, celShadow(TILE_PALETTE.masonryBase, 0.52));

  // Fiadas de altura irregular, começando **acima** do quadro sempre — inclusive quando há
  // coroamento. A pedra do corpo tem que ser cortada pela borda do tile para emendar com a
  // do tile de cima, e o coroamento é pintado por cima dela no fim: onde a faixa afina
  // (numa encosta em diagonal, ver `Crown`) o que aparece por baixo é pedra, e não o vão
  // escuro que uma fiada começando abaixo da faixa deixaria.
  let y = -3 - ((variant * 3) % 5);
  let course = 0;

  while (y < TILE_SIZE) {
    const height = 6 + Math.floor(hash01(course, variant, 41) * 3);
    // Deslocamento por fiada, para as juntas verticais não empilharem.
    let x = -4 - Math.floor(hash01(course, variant, 43) * 8);

    while (x < TILE_SIZE) {
      const width = 7 + Math.floor(hash01(x + 90, course, 31 + variant) * 8);
      const roll = hash01(x + 50, course * 7, 37 + variant);
      // Faixa curta de propósito. O sorteio antigo ia até +0,28, e a pedra mais clara que ele
      // produzia subia até o valor do calçamento — bastava uma pedra em cada duas fiadas cair
      // no topo do sorteio para o muro voltar a se dissolver na praça. Aqui o corpo inteiro
      // cabe abaixo da lajota mais escura do chão, e a variação que sobra ainda é suficiente
      // para nenhuma pedra ser igual à vizinha.
      const body = shade(TILE_PALETTE.masonryBase, -0.06 + roll * 0.2);

      roundRect(ctx, x, y, width - 1, height - 1, 1, body);
      // Quina iluminada em cima e à esquerda, sombra na de baixo: uma pedra plana lê
      // como azulejo.
      roundRect(ctx, x, y, width - 1, 1.1, 0.5, celLight(body, 0.3));
      roundRect(ctx, x, y + height - 2.1, width - 1, 1, 0.5, celShadow(body, 0.26));

      x += width;
    }

    y += height;
    course++;
  }

  // Coroamento: a face de cima da parede, vista de esguelha. A borda de baixo dela é
  // irregular de propósito — reta, ela vira a mesma listra de antes, só que uma vez só —, e
  // a largura dela acompanha a rocha vizinha (ver `Crown`).
  paintCrown(ctx, crown, variant, 7, 47, TILE_PALETTE.masonryTop, TILE_PALETTE.masonryLight);

  if (foot) wallFoot(ctx);

  // Musgo em algumas variações: o mundo parece habitado quando a pedra tem idade. Ele
  // nasce no pé e nas juntas, que é onde a umidade fica.
  if (variant % 3 === 1) {
    for (let i = 0; i < 7; i++) {
      const mx = Math.floor(hash01(i, variant, 811) * (TILE_SIZE - 3));
      const my = 18 + Math.floor(hash01(i, variant, 812) * 12);
      px(ctx, mx, my, 2, 1, TILE_PALETTE.wallMoss);
      px(ctx, mx, my - 1, 1, 1, celLight(TILE_PALETTE.wallMoss, 0.3));
    }
  }
}

/**
 * O pé da face, onde a parede encontra o chão.
 *
 * Um degradê de três pixels, e não uma faixa chapada de quatro. A faixa chapada é o
 * segundo motivo do aspecto listrado: ela tem borda de cima nítida, e essa borda é uma
 * linha reta atravessando o tile — indistinguível da linha de uma grade. Degradê não tem
 * borda, então o que se lê é a sombra que a própria parede projeta em si mesma.
 */
function wallFoot(ctx: CanvasRenderingContext2D): void {
  for (let i = 0; i < 5; i++) {
    ctx.globalAlpha = 0.16 + i * 0.16;
    px(ctx, 0, TILE_SIZE - 1 - i, TILE_SIZE, 1, TILE_PALETTE.wallDeep);
  }
  ctx.globalAlpha = 1;
}

/**
 * Rocha bruta: encosta de montanha e parede de caverna.
 *
 * Mesma leitura de altura da alvenaria — topo claro, pé escuro —, e nenhuma fiada. O que
 * substitui o tijolo são blocos irregulares de tamanho e tom sorteados, encaixados sem
 * alinhamento: é a ausência de linha reta que diz "isto não foi assentado por ninguém".
 */
function drawCliff(
  ctx: CanvasRenderingContext2D,
  variant: number,
  crown: Crown,
  foot: boolean,
): void {
  // O fundo é o vão entre as lascas, e ele é escuro **mas não muito** — pela mesma razão
  // que a junta da alvenaria não é preta. Ver `FACETS`: onde o sorteio deixa um pedaço da
  // célula descoberto, é este tom que aparece, e quanto mais longe ele estiver da rocha
  // mais aquela célula se anuncia como célula.
  px(ctx, 0, 0, TILE_SIZE, TILE_SIZE, celShadow(TILE_PALETTE.wallBase, 0.2));

  /**
   * Facetas: lascas irregulares, e não blocos.
   *
   * A diferença com a alvenaria tem que estar na **forma**, e não só na ausência de
   * fiada. Retângulos de cantos redondos empilhados dão tijolo com quina gasta — que foi
   * exatamente o que a primeira tentativa produziu, uma encosta que parecia muro. O que
   * lê como rocha é o polígono de lados desiguais: cinco vértices sorteados num anel, sem
   * nenhum lado paralelo a outro.
   *
   * A luz entra só na aresta voltada para o noroeste, e não como faixa em cima e embaixo
   * de cada peça: faixa horizontal em toda faceta recria fiada por outro caminho.
   *
   * ## Por que são vinte e seis, e não dezoito
   *
   * A cobertura da célula é sorteada, e com dezoito lascas ela variava o bastante entre uma
   * variação e outra para as células ficarem com **tons médios diferentes**. Numa parede de
   * rocha maciça — que é metade da área da caverna — isso reaparece como um xadrez de
   * quadrados claros e escuros de 32 px: exatamente o quadriculado que a arte inteira existe
   * para não desenhar, só que uma escala acima da grade de juntas que a primeira versão
   * produzia. Mais lascas fazem a cobertura saturar, e a saturação é o que faz seis
   * variações terem a mesma densidade sem terem o mesmo desenho.
   */
  for (let i = 0; i < 26; i++) {
    const x = Math.floor(hash01(i, variant, 2101) * (TILE_SIZE + 8)) - 4;
    // Sempre a partir de cima do quadro: o coroamento é pintado por cima no fim, e onde ele
    // afina o que aparece por baixo tem que ser lasca de rocha, e não fundo chapado.
    const y = -8 + Math.floor(hash01(i, variant, 2103) * (TILE_SIZE + 4));
    const rx = 6 + hash01(i, variant, 2105) * 7;
    const ry = 5 + hash01(i, variant, 2107) * 6;

    const roll = hash01(i, variant, 2109);
    const body = shade(TILE_PALETTE.wallBase, -0.2 + roll * 0.38);

    const facet: Shape = (c) => {
      c.beginPath();
      for (let v = 0; v < 5; v++) {
        const angle = (v / 5) * TAU + hash01(i * 5 + v, variant, 2115) * 0.9;
        const reach = 0.7 + hash01(i * 5 + v, variant, 2117) * 0.55;
        const vx = x + Math.cos(angle) * rx * reach;
        const vy = y + Math.sin(angle) * ry * reach;
        if (v === 0) c.moveTo(vx, vy);
        else c.lineTo(vx, vy);
      }
      c.closePath();
    };

    ctx.fillStyle = celShadow(body, 0.34);
    facet(ctx);
    ctx.fill();

    // A face iluminada é a mesma lasca puxada para o noroeste: a franja de sombra que
    // sobra na borda inferior direita é o que dá volume à pedra.
    clipped(ctx, facet, (c) => {
      c.translate(-1.5, -1.5);
      c.fillStyle = body;
      facet(c);
      c.fill();
    });
  }

  // Topo iluminado, como na alvenaria: é o que dá altura à face. A borda de baixo é
  // irregular, e a largura da faixa acompanha a rocha vizinha — é ela que transforma a
  // escadinha de degraus luminosos numa crista descendo em diagonal (ver `Crown`).
  paintCrown(ctx, crown, variant, 5, 2111, TILE_PALETTE.wallTop, TILE_PALETTE.wallLight);

  if (foot) wallFoot(ctx);

  // Musgo e trincas: idade. Sem elas a rocha vira granito polido.
  for (let i = 0; i < 4; i++) {
    const x = Math.floor(hash01(i, variant, 2201) * (TILE_SIZE - 3));
    const y = 6 + Math.floor(hash01(i, variant, 2203) * (TILE_SIZE - 14));
    const length = 3 + Math.floor(hash01(i, variant, 2205) * 5);

    for (let step = 0; step < length; step++) {
      px(ctx, x + Math.round(Math.sin(step * 1.3) * 1.4), y + step, 1, 1, TILE_PALETTE.wallDeep);
    }
  }

  if (variant % 3 === 1) {
    for (let i = 0; i < 5; i++) {
      const mx = Math.floor(hash01(i, variant, 2301) * (TILE_SIZE - 3));
      const my = 18 + Math.floor(hash01(i, variant, 2303) * 10);
      px(ctx, mx, my, 3, 1, TILE_PALETTE.wallMoss);
      px(ctx, mx, my - 1, 2, 1, celLight(TILE_PALETTE.wallMoss, 0.28));
    }
  }
}

/**
 * Rocha com veio de minério.
 *
 * É a rocha bruta com um enxame de cristais numa faixa diagonal. A faixa é o ponto: minério
 * não se distribui uniformemente pela pedra, ele corre numa direção — e é essa direção,
 * repetida em tiles vizinhos, que faz o olho ler "veio" em vez de "pedra com pintinhas".
 *
 * A veia atravessa o tile de canto a canto de propósito, sempre na mesma inclinação: dois
 * tiles de minério lado a lado emendam a faixa, e a galeria inteira passa a ter uma
 * direção de filão.
 */
function drawOre(
  ctx: CanvasRenderingContext2D,
  variant: number,
  crown: Crown,
  foot: boolean,
): void {
  drawCliff(ctx, variant, crown, foot);

  const offset = (variant % 3) * 9 - 6;

  for (let step = 0; step < TILE_SIZE; step++) {
    // A faixa desce a 45° com uma ondulação curta: reta demais lê como risco de caneta.
    const x = step;
    const y = step + offset + Math.round(Math.sin(step * 0.55) * 2.5);
    if (y < 0 || y >= TILE_SIZE) continue;

    // A veia é uma trilha de pepitas, e não uma linha contínua: contínua vira arame.
    if (hash01(step, variant, 3601) > 0.62) continue;

    const size = hash01(step, variant, 3602) < 0.3 ? 2 : 1;

    px(ctx, x, y + 1, size, size, TILE_PALETTE.oreDark);
    px(ctx, x, y, size, size, TILE_PALETTE.oreBase);
    px(ctx, x, y, 1, 1, TILE_PALETTE.oreLight);
  }

  // Duas pepitas maiores fora da faixa: é o que impede a diagonal de virar um padrão
  // geométrico perfeito, que nenhuma pedra tem.
  for (let i = 0; i < 2; i++) {
    const x = 3 + Math.floor(hash01(i, variant, 3611) * (TILE_SIZE - 8));
    const y = 3 + Math.floor(hash01(i, variant, 3612) * (TILE_SIZE - 8));

    px(ctx, x, y + 1, 3, 2, TILE_PALETTE.oreDark);
    px(ctx, x, y, 3, 2, TILE_PALETTE.oreBase);
    px(ctx, x, y, 1, 1, TILE_PALETTE.oreLight);
  }
}

/**
 * Escada.
 *
 * Subir e descer **não** são o mesmo desenho invertido, que era o que a primeira versão
 * fazia: degraus espelhados leem igual de relance, e a única diferença — para que lado o
 * degrau largo aponta — exige comparar os dois lado a lado, coisa que ninguém faz no meio
 * de uma perseguição. Aqui a diferença é de **luz**: a escada que sobe é um bloco de
 * pedra clara saindo do chão, a que desce é um buraco escuro entrando nele. Claro contra
 * escuro se lê sem pensar.
 */
function drawStairs(ctx: CanvasRenderingContext2D, up: boolean): void {
  // Moldura de pedra, comum às duas.
  px(ctx, 0, 0, TILE_SIZE, TILE_SIZE, TILE_PALETTE.wallDark);
  px(ctx, 1, 1, TILE_SIZE - 2, TILE_SIZE - 2, TILE_PALETTE.wallBase);
  px(ctx, 1, 1, TILE_SIZE - 2, 1, TILE_PALETTE.wallLight);

  const steps = 5;
  const stepHeight = (TILE_SIZE - 6) / steps;

  for (let step = 0; step < steps; step++) {
    const y = 3 + step * stepHeight;

    if (up) {
      // Degraus que estreitam para o fundo, cada um mais claro que o anterior: o olho
      // sobe do escuro (perto) para o claro (longe).
      const inset = 3 + step * 2;
      const body = shade(TILE_PALETTE.wallTop, -0.24 + (step / steps) * 0.5);

      px(ctx, inset, y, TILE_SIZE - inset * 2, stepHeight - 1, body);
      px(ctx, inset, y, TILE_SIZE - inset * 2, 1, shade(body, 0.28));
      px(ctx, inset, y + stepHeight - 2, TILE_SIZE - inset * 2, 1, shade(body, -0.3));
      continue;
    }

    // Descida: o poço escurece para o fundo, e o degrau alarga — é a boca do buraco que
    // fica perto do observador.
    const inset = 3 + (steps - 1 - step) * 2;
    const body = shade(TILE_PALETTE.wallDark, 0.34 - (step / steps) * 0.34);

    px(ctx, inset, y, TILE_SIZE - inset * 2, stepHeight - 1, body);
    px(ctx, inset, y, TILE_SIZE - inset * 2, 1, shade(body, 0.22));
  }

  if (!up) {
    // Sombra do vão no fundo do poço: sem ela o buraco lê como pilha de degraus.
    ctx.globalAlpha = 0.55;
    px(ctx, 5, TILE_SIZE - 8, TILE_SIZE - 10, 5, '#000000');
    ctx.globalAlpha = 1;
  }
}

// --- Mecanismos --------------------------------------------------------------

/**
 * Porta, nos três estados (PRD §15.2, §15.4).
 *
 * A diferença entre os três precisa ser vista **antes** do clique, e por isso ela é de
 * forma e não de cor: a fechada é uma folha de madeira inteira, a aberta é o vão escuro com
 * a folha recolhida contra o batente, e a trancada é a fechada mais um cadeado de latão.
 * Um jogador que não distingue "não abre porque está trancada" de "não abre porque está
 * quebrada" reclama de bug — e estava certo, porque até aqui as três eram calçamento.
 */
function drawDoor(
  ctx: CanvasRenderingContext2D,
  variant: number,
  state: 'closed' | 'open' | 'locked',
): void {
  // O batente é pedra: a porta está numa parede, e o que sobra dela nas beiradas do tile é
  // o que emenda com a alvenaria dos lados.
  px(ctx, 0, 0, TILE_SIZE, TILE_SIZE, TILE_PALETTE.wallDark);
  px(ctx, 2, 0, TILE_SIZE - 4, TILE_SIZE, celShadow(TILE_PALETTE.wallDeep, 0.2));

  if (state === 'open') {
    // Vão escuro, com a folha recolhida contra o batente da esquerda. O escuro é o que se
    // lê de longe como "dá para passar".
    px(ctx, 3, 1, TILE_SIZE - 6, TILE_SIZE - 2, '#0d0b0a');
    px(ctx, 3, 1, 5, TILE_SIZE - 2, TILE_PALETTE.woodDark);
    px(ctx, 3, 1, 5, 2, TILE_PALETTE.woodBase);
    px(ctx, 4, 3, 1, TILE_SIZE - 6, TILE_PALETTE.woodLight);

    // Luz entrando pelo vão: a mancha no chão é o que faz o buraco ler como passagem para
    // um lugar, e não como poço.
    ctx.globalAlpha = 0.16;
    px(ctx, 9, TILE_SIZE - 8, TILE_SIZE - 12, 7, '#ffe9c0');
    ctx.globalAlpha = 1;
    return;
  }

  // Folha de madeira: pranchas verticais com um vão escuro entre elas, como a junta da
  // alvenaria — e pelo mesmo motivo, que é não desenhar linha nenhuma à mão.
  px(ctx, 3, 1, TILE_SIZE - 6, TILE_SIZE - 2, TILE_PALETTE.woodDark);

  for (let x = 4; x < TILE_SIZE - 4; x += 6) {
    const width = Math.min(5, TILE_SIZE - 4 - x);
    const tone = shade(TILE_PALETTE.woodBase, -0.08 + hash01(x, variant, 3701) * 0.2);

    px(ctx, x, 2, width, TILE_SIZE - 4, tone);
    px(ctx, x, 2, width, 1, celLight(tone, 0.28));
    px(ctx, x, TILE_SIZE - 3, width, 1, celShadow(tone, 0.3));
    // Veio da madeira: dois traços por prancha, sempre nos mesmos lugares.
    for (let i = 0; i < 2; i++) {
      const y = 5 + Math.floor(hash01(x + i, variant, 3702) * (TILE_SIZE - 12));
      px(ctx, x + 1, y, width - 2, 1, celShadow(tone, 0.16));
    }
  }

  // Travessas de ferro: são elas que dizem "porta" e não "cerca".
  for (const y of [6, TILE_SIZE - 9]) {
    px(ctx, 3, y, TILE_SIZE - 6, 3, TILE_PALETTE.ironBase);
    px(ctx, 3, y, TILE_SIZE - 6, 1, TILE_PALETTE.ironLight);
    px(ctx, 3, y + 2, TILE_SIZE - 6, 1, TILE_PALETTE.ironDark);
  }

  // Maçaneta à direita, sempre: uma porta com a maçaneta sorteada por variação viraria um
  // catálogo de portas diferentes na mesma rua.
  ellipse(ctx, TILE_SIZE - 8, TILE_SIZE / 2, 2, 2, TILE_PALETTE.ironDark);
  ellipse(ctx, TILE_SIZE - 8, TILE_SIZE / 2 - 1, 1.6, 1.6, TILE_PALETTE.ironLight);

  if (state === 'locked') {
    // Cadeado de latão no meio: cor que não aparece em mais nada da porta, na altura do
    // olho. É o único sinal, e por isso é grande.
    const cx = TILE_SIZE / 2;
    const cy = TILE_SIZE / 2;

    px(ctx, cx - 3, cy - 5, 6, 4, TILE_PALETTE.ironDark);
    px(ctx, cx - 2, cy - 6, 4, 2, TILE_PALETTE.ironLight);
    roundRect(ctx, cx - 5, cy - 2, 10, 9, 2, TILE_PALETTE.brass);
    roundRect(ctx, cx - 5, cy - 2, 10, 2, 1, celLight(TILE_PALETTE.brass, 0.3));
    px(ctx, cx - 1, cy + 1, 2, 4, TILE_PALETTE.ironDark);
  }
}

/**
 * Buraco no chão: o que faz descer sem escada (PRD §15.4).
 *
 * Um disco preto com a boca de pedra em volta e nada mais. A tentação seria desenhar o que
 * há embaixo; não há como — o cliente não conhece o andar de baixo —, e a mancha preta é
 * mais honesta: buraco é a ausência de chão.
 *
 * A boca é **irregular** e o preto é chapado, porque o que precisa acontecer nesse tile é o
 * olho parar. Um buraco com degradê suave some no chão de caverna, que é da mesma família
 * de cinza.
 */
function drawHole(ctx: CanvasRenderingContext2D, variant: number): void {
  drawCaveFloor(ctx, variant);

  const cx = TILE_SIZE / 2;
  const cy = TILE_SIZE / 2 + 1;

  // Lábio de pedra levantado em volta: entulho da queda, mais claro no lado noroeste.
  ellipse(ctx, cx, cy, 13, 10, celShadow(TILE_PALETTE.caveRamp[0] ?? '#514c45', 0.2));
  ellipse(ctx, cx - 1, cy - 1, 12.5, 9.5, celLight(TILE_PALETTE.caveRamp[3] ?? '#69645a', 0.14));

  // O vazio. Duas elipses: a de fora é a parede do poço, a de dentro é o fundo que não se vê.
  ellipse(ctx, cx, cy, 10.5, 7.6, '#221f1c');
  ellipse(ctx, cx, cy + 0.5, 8.5, 5.8, '#000000');

  // Pedrisco na beirada, para a boca não ficar com contorno de compasso.
  for (let i = 0; i < 7; i++) {
    const angle = (i / 7) * TAU + hash01(i, variant, 3801) * 0.6;
    const x = Math.round(cx + Math.cos(angle) * 11.5);
    const y = Math.round(cy + Math.sin(angle) * 8.5);

    px(ctx, x, y, 2, 2, TILE_PALETTE.rockShade);
    px(ctx, x, y, 1, 1, TILE_PALETTE.rockLight);
  }
}

/**
 * Alavanca: haste de ferro sobre base de pedra, sempre na mesma posição.
 *
 * Sem variação de posição, e é de propósito: a alavanca é o gatilho de uma charada, e o
 * jogador precisa reconhecê-la de relance no meio de uma parede — não é o lugar para o
 * ruído que dá vida à grama.
 */
function drawLever(ctx: CanvasRenderingContext2D, variant: number): void {
  drawPavement(ctx, variant);

  // Placa de base, com sombra: é ela que assenta o mecanismo na parede.
  roundRect(ctx, 9, 8, 14, 18, 2, TILE_PALETTE.ironDark);
  roundRect(ctx, 10, 9, 12, 15, 2, TILE_PALETTE.ironBase);
  roundRect(ctx, 10, 9, 12, 2, 1, TILE_PALETTE.ironLight);

  // Haste na diagonal, com o punho de madeira em cima: a diagonal é o que diz "isto se
  // move", contra a vertical de tudo o mais que é parede.
  for (let step = 0; step < 12; step++) {
    px(ctx, 13 + Math.round(step * 0.55), 22 - step, 2, 2, TILE_PALETTE.ironLight);
  }

  ellipse(ctx, 20, 10, 3.4, 3, TILE_PALETTE.woodDark);
  ellipse(ctx, 19.4, 9.4, 2.6, 2.2, TILE_PALETTE.woodBase);
  px(ctx, 18, 8, 2, 1, TILE_PALETTE.woodLight);
}

/**
 * Baú: madeira com aro de ferro e fechadura de latão.
 *
 * Ele é a única peça do cenário que promete **recompensa**, e por isso é a mais brilhante:
 * o latão da fechadura é a mesma cor da porta trancada, e é a cor que o jogador já aprendeu
 * a associar a "isto guarda alguma coisa".
 */
function drawChest(ctx: CanvasRenderingContext2D, variant: number): void {
  drawPavement(ctx, variant);

  const left = 5;
  const top = 9;
  const width = TILE_SIZE - 10;
  const height = 17;

  // Sombra no chão: sem ela o baú flutua sobre o calçamento.
  ctx.globalAlpha = 0.35;
  ellipse(ctx, TILE_SIZE / 2, top + height, width * 0.55, 3, '#000000');
  ctx.globalAlpha = 1;

  // Corpo, com as pranchas horizontais.
  roundRect(ctx, left, top, width, height, 2, TILE_PALETTE.woodDark);
  roundRect(ctx, left + 1, top + 1, width - 2, height - 2, 2, TILE_PALETTE.woodBase);

  for (let y = top + 8; y < top + height - 2; y += 4) {
    px(ctx, left + 2, y, width - 4, 1, TILE_PALETTE.woodDark);
  }

  // Tampa abaulada: a curva é o que separa um baú de uma caixa.
  roundRect(ctx, left, top, width, 8, 3, TILE_PALETTE.woodLight);
  roundRect(ctx, left + 1, top + 1, width - 2, 3, 2, celLight(TILE_PALETTE.woodLight, 0.24));
  px(ctx, left, top + 7, width, 1, TILE_PALETTE.woodDark);

  // Aros de ferro nas duas pontas.
  for (const x of [left + 2, left + width - 5]) {
    px(ctx, x, top + 1, 3, height - 2, TILE_PALETTE.ironBase);
    px(ctx, x, top + 1, 1, height - 2, TILE_PALETTE.ironLight);
    px(ctx, x + 2, top + 1, 1, height - 2, TILE_PALETTE.ironDark);
  }

  // Fechadura no meio da junta da tampa.
  px(ctx, TILE_SIZE / 2 - 2, top + 5, 5, 6, TILE_PALETTE.brass);
  px(ctx, TILE_SIZE / 2 - 2, top + 5, 5, 1, celLight(TILE_PALETTE.brass, 0.35));
  px(ctx, TILE_SIZE / 2, top + 7, 1, 2, TILE_PALETTE.ironDark);
}

// --- Água --------------------------------------------------------------------

/**
 * Marolas curtas, e não ondas que atravessam o tile.
 *
 * A versão anterior desenhava uma senoide que fechava um ciclo exato por tile, para que
 * a crista continuasse no vizinho sem emenda. O raciocínio estava certo e o resultado,
 * errado: **todo** tile de água ficava com a mesma crista no mesmo lugar, e um lago
 * grande virava papel de parede — o olho lê a repetição de 32 px antes de ler a água.
 *
 * Aqui cada tile recebe um punhado de traços curtos, sorteados pela sua variação, que
 * descem devagar com a fase. Não há continuidade de crista entre tiles vizinhos, e é
 * justamente por isso que não há grade visível: seis variações espalhadas pelo lago não
 * têm padrão que o olho consiga fechar.
 */
const RIPPLES = 5;

function makeWaterBase(variant: number): HTMLCanvasElement {
  const canvas = createCanvas(TILE_SIZE, TILE_SIZE);
  const ctx = context2d(canvas);

  const ramp = [
    TILE_PALETTE.waterDeep,
    TILE_PALETTE.waterDeep,
    TILE_PALETTE.waterBase,
    TILE_PALETTE.waterMid,
  ];

  fillRamp(ctx, ramp, variant, 401);
  return canvas;
}

function paintWater(
  ctx: CanvasRenderingContext2D,
  anim: TilesetAnim,
  variant: number,
  phase: number,
): void {
  const base = anim.waterBases[variant];
  if (base) ctx.drawImage(base, 0, 0);

  for (let i = 0; i < RIPPLES; i++) {
    const x = Math.floor(hash01(i, variant, 431) * TILE_SIZE);
    // A deriva é vertical e lenta: o resto do cenário é estático, e água que corre
    // depressa demais puxa o olho para longe do que importa, que é o combate.
    const drift = hash01(i, variant, 433) + phase * 0.5;
    const y = Math.floor((drift % 1) * TILE_SIZE);
    const length = 3 + Math.floor(hash01(i, variant, 435) * 4);

    ctx.globalAlpha = 0.7;
    px(ctx, x, y, length, 1, TILE_PALETTE.waterLight);
    ctx.globalAlpha = 0.35;
    px(ctx, x + 1, y + 1, length - 1, 1, TILE_PALETTE.waterDeep);
    ctx.globalAlpha = 1;
  }

  // Cintilações que acendem e apagam fora de fase entre si: é o "sol na água" — some
  // se piscar tudo junto.
  for (let i = 0; i < 2; i++) {
    const sx = 2 + Math.floor(hash01(i, variant, 501) * (TILE_SIZE - 4));
    const sy = 2 + Math.floor(hash01(i, variant, 502) * (TILE_SIZE - 4));
    const twinkle = (hash01(i, variant, 503) + phase) % 1;
    if (twinkle >= 0.25) continue;

    px(ctx, sx, sy, 1, 1, TILE_PALETTE.waterSparkle);
    if (twinkle < 0.1) {
      ctx.globalAlpha = 0.5;
      px(ctx, sx - 1, sy, 1, 1, TILE_PALETTE.waterSparkle);
      px(ctx, sx + 1, sy, 1, 1, TILE_PALETTE.waterSparkle);
      ctx.globalAlpha = 1;
    }
  }
}

// --- Portal ------------------------------------------------------------------

function makePortalGround(variant: number): HTMLCanvasElement {
  const canvas = createCanvas(TILE_SIZE, TILE_SIZE);
  drawPavement(context2d(canvas), variant);
  return canvas;
}

/**
 * Portal entre regiões: um anel luminoso que pulsa sobre o piso.
 *
 * Sobre o piso, e não substituindo-o, porque o portal é uma marca no cenário e não um
 * terreno próprio. O pulso usa a mesma fase da água: um relógio só para todo o tileset.
 */
function paintPortalRings(ctx: CanvasRenderingContext2D, phase: number): void {
  const cx = TILE_SIZE / 2;
  const cy = TILE_SIZE / 2;
  const pulse = 0.5 + 0.5 * Math.sin(phase * TAU);

  // Halo, corpo e núcleo. O halo ocupa quase o tile inteiro: o portal é o único ponto do
  // mapa que teleporta, e precisa ser visto do outro lado da tela — um disco de 8 px, que
  // era o tamanho anterior, some entre as lajotas.
  ctx.globalAlpha = 0.22 + 0.18 * pulse;
  ellipse(ctx, cx, cy, 15 + pulse * 1.5, 11 + pulse, TILE_PALETTE.portalGlow);
  ctx.globalAlpha = 0.55;
  ellipse(ctx, cx, cy, 12, 8.5, TILE_PALETTE.portalCore);
  ctx.globalAlpha = 0.9;
  ellipse(ctx, cx, cy, 8, 5.5, TILE_PALETTE.portalCore);
  ctx.globalAlpha = 1;
  ellipse(ctx, cx, cy, 4 + pulse, 3 + pulse * 0.6, TILE_PALETTE.portalGlow);
  ellipse(ctx, cx - 1, cy - 1, 2, 1.4, '#ffffff');

  // Fagulhas orbitando: vendem o movimento mesmo para quem só vê o portal de relance.
  for (let i = 0; i < 5; i++) {
    const angle = phase * TAU + (i * TAU) / 5;
    px(
      ctx,
      Math.round(cx + Math.cos(angle) * 12),
      Math.round(cy + Math.sin(angle) * 8.5),
      2,
      2,
      TILE_PALETTE.portalGlow,
    );
  }
}

// --- Transições --------------------------------------------------------------

const EDGE_COLUMNS = 32;

/**
 * Dois blocos: 256 máscaras de transbordo por material, e depois 16 de quina cortada.
 *
 * O segundo bloco entra **no fim** e não intercalado, pela mesma razão de sempre neste
 * repositório: o índice do primeiro é aritmética publicada (`edgeFrame`), e enfiar quadros no
 * meio renumeraria em silêncio tudo o que vem depois.
 */
const EDGE_FRAMES = MATERIAL_COUNT * 256 + MATERIAL_COUNT * CUT_SHAPES;
const EDGE_ROWS = Math.ceil(EDGE_FRAMES / EDGE_COLUMNS);

/**
 * Profundidade da borda na coluna (ou linha) `i`.
 *
 * Duas frequências somadas: uma por pixel, que dá o serrilhado fino, e uma a cada três
 * pixels, que dá a ondulação larga. Só a fina produziria uma franja regular como dente
 * de serra; só a larga, uma curva lisa demais para ler como terra invadindo grama.
 *
 * O perfil depende apenas da posição **dentro** do tile, e não da posição no mundo — é o
 * que faz a borda continuar sem emenda de um tile para o vizinho.
 */
function edgeProfile(i: number, salt: number): number {
  const fine = hash01(i, 0, salt);
  const broad = hash01(Math.floor(i / 3), 1, salt + 13);
  return 6 + Math.round(fine * 4 + broad * 6);
}

/** Uma peça de borda: o material recortado numa faixa irregular junto a um dos lados. */
function makeSidePiece(material: HTMLCanvasElement, side: number): HTMLCanvasElement {
  const canvas = createCanvas(TILE_SIZE, TILE_SIZE);
  const ctx = context2d(canvas);
  ctx.drawImage(material, 0, 0);

  const mask = createCanvas(TILE_SIZE, TILE_SIZE);
  const maskCtx = context2d(mask);

  for (let i = 0; i < TILE_SIZE; i++) {
    const depth = edgeProfile(i, 3001 + side * 17);

    if (side === 0) px(maskCtx, i, 0, 1, depth, '#fff');
    else if (side === 1) px(maskCtx, TILE_SIZE - depth, i, depth, 1, '#fff');
    else if (side === 2) px(maskCtx, i, TILE_SIZE - depth, 1, depth, '#fff');
    else px(maskCtx, 0, i, depth, 1, '#fff');
  }

  // Uma composição só: `destination-in` aplicado a cada retângulo apagaria o que os
  // anteriores tinham preservado.
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(mask, 0, 0);

  return canvas;
}

/**
 * Peça de canto, para a diagonal que os lados não cobrem.
 *
 * Sem ela, dois materiais que se tocam só na quina deixam um quadrado nítido de 32 px
 * sem transição — o defeito mais visível de um autotile incompleto.
 */
function makeCornerPiece(material: HTMLCanvasElement, corner: number): HTMLCanvasElement {
  const canvas = createCanvas(TILE_SIZE, TILE_SIZE);
  const ctx = context2d(canvas);
  ctx.drawImage(material, 0, 0);

  const mask = createCanvas(TILE_SIZE, TILE_SIZE);
  const maskCtx = context2d(mask);

  // Cantos na ordem da máscara: 0 = nordeste, 1 = sudeste, 2 = sudoeste, 3 = noroeste.
  const originX = corner === 0 || corner === 1 ? TILE_SIZE : 0;
  const originY = corner === 1 || corner === 2 ? TILE_SIZE : 0;

  for (let y = 0; y < TILE_SIZE; y++) {
    for (let x = 0; x < TILE_SIZE; x++) {
      const dx = x + 0.5 - originX;
      const dy = y + 0.5 - originY;
      const radius = 8 + hash01(x, y, 3301 + corner) * 4;
      if (dx * dx + dy * dy < radius * radius) px(maskCtx, x, y, 1, 1, '#fff');
    }
  }

  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(mask, 0, 0);

  return canvas;
}

/**
 * Textura das transições: 256 combinações de vizinhança por material.
 *
 * Os bits da máscara seguem a rosa dos ventos a partir do norte, em sentido horário:
 * N=1, NE=2, L=4, SE=8, S=16, SO=32, O=64, NO=128. O canto só é desenhado quando **nem**
 * um nem outro dos lados vizinhos está presente — se estiverem, já cobriram a quina, e
 * empilhar a peça de canto por cima só escureceria a emenda.
 */
function buildEdges(scene: Phaser.Scene): void {
  if (scene.textures.exists(EDGES_TEXTURE)) return;

  const canvas = createCanvas(TILE_SIZE * EDGE_COLUMNS, TILE_SIZE * EDGE_ROWS);
  const ctx = context2d(canvas);

  const drawers: Record<number, (c: CanvasRenderingContext2D, v: number) => void> = {
    // A grama entra na tabela **só** por causa do corte de quina: nada é mais fraco que ela,
    // então ela nunca transborda sobre coisa nenhuma — mas é o material que cerca a quina
    // convexa da esmagadora maioria das poças, lagos e estradas do mundo aberto, e sem ela
    // o arredondamento não teria com o que morder.
    [Material.Grass]: drawGrass,
    [Material.Dirt]: drawDirt,
    [Material.Sand]: drawSand,
    [Material.Pavement]: drawPavement,
    [Material.Cave]: drawCaveFloor,
    [Material.Water]: (c, v) => drawShoreWater(c, v),
    // A borda da lava é a **crosta esfriando**, e não a lava do meio da poça: é ali que ela
    // encontra a rocha, e é por isso que a peça de transição é mais escura que o material.
    // Uma borda cor de brasa transbordando sobre a galeria acenderia a caverna inteira.
    [Material.Lava]: drawLavaShore,
  };

  for (let material = 0; material < MATERIAL_COUNT; material++) {
    const draw = drawers[material];
    if (!draw) continue;

    const source = createCanvas(TILE_SIZE, TILE_SIZE);
    draw(context2d(source), 0);

    const corners = range(4).map((corner) => makeCornerPiece(source, corner));

    // O corte de quina (ver `cutFrame`): as mesmas quatro peças de canto, agora usadas para
    // arredondar o material forte em vez de preencher a diagonal do fraco.
    for (let cuts = 1; cuts < CUT_SHAPES; cuts++) {
      const frame = cutFrame(material as Material, cuts);

      ctx.save();
      ctx.translate((frame % EDGE_COLUMNS) * TILE_SIZE, Math.floor(frame / EDGE_COLUMNS) * TILE_SIZE);

      for (let corner = 0; corner < 4; corner++) {
        if ((cuts & (1 << corner)) === 0) continue;
        const piece = corners[corner];
        if (piece) ctx.drawImage(piece, 0, 0);
      }

      ctx.restore();
    }

    // Transbordo só existe para quem tem alguém mais fraco por baixo. A grama não tem, e
    // gerar 255 máscaras que ninguém pede custaria mil composições na subida do cliente.
    if (material === Material.Grass) continue;

    const sides = range(4).map((side) => makeSidePiece(source, side));

    for (let mask = 1; mask < 256; mask++) {
      const frame = material * 256 + mask;
      const column = frame % EDGE_COLUMNS;
      const row = Math.floor(frame / EDGE_COLUMNS);

      ctx.save();
      ctx.translate(column * TILE_SIZE, row * TILE_SIZE);

      for (let side = 0; side < 4; side++) {
        if ((mask & (1 << (side * 2))) === 0) continue;
        const piece = sides[side];
        if (piece) ctx.drawImage(piece, 0, 0);
      }

      for (let corner = 0; corner < 4; corner++) {
        const bit = 1 << (corner * 2 + 1);
        if ((mask & bit) === 0) continue;

        // Lados vizinhos da diagonal: NE toca N e L, SE toca L e S, e assim por diante.
        const before = 1 << (corner * 2);
        const after = 1 << (((corner + 1) % 4) * 2);
        if ((mask & before) !== 0 || (mask & after) !== 0) continue;

        const piece = corners[corner];
        if (piece) ctx.drawImage(piece, 0, 0);
      }

      ctx.restore();
    }
  }

  addGridTexture(scene, EDGES_TEXTURE, canvas, TILE_SIZE, TILE_SIZE, EDGE_COLUMNS, EDGE_ROWS);
}

/**
 * A água, quando invade a terra, não é a mesma água do meio do lago: é a lâmina rasa da
 * margem, mais clara, com espuma. Desenhá-la como o azul profundo faria o lago parecer
 * um degrau — a margem é justamente onde a profundidade acaba.
 */
function drawShoreWater(ctx: CanvasRenderingContext2D, variant: number): void {
  fillRamp(
    ctx,
    [TILE_PALETTE.waterMid, TILE_PALETTE.waterLight, TILE_PALETTE.waterLight, TILE_PALETTE.waterSparkle],
    variant,
    1201,
  );

  for (let i = 0; i < 26; i++) {
    const x = Math.floor(hash01(i, variant, 1301) * TILE_SIZE);
    const y = Math.floor(hash01(i, variant, 1302) * TILE_SIZE);
    px(ctx, x, y, 2, 1, TILE_PALETTE.waterFoam);
  }
}

/**
 * A margem da lava: crosta escura com a fresta quente ainda aparecendo.
 *
 * O mesmo argumento da lâmina rasa da água, invertido. A água clareia na margem porque
 * fica rasa; a lava **escurece** porque esfria ao encostar na rocha — e é essa borda escura
 * com veio vermelho que dá à poça a leitura de crosta, em vez da de mancha de tinta.
 */
function drawLavaShore(ctx: CanvasRenderingContext2D, variant: number): void {
  fillRamp(
    ctx,
    [TILE_PALETTE.lavaCrust, TILE_PALETTE.lavaCrust, TILE_PALETTE.lavaDeep, TILE_PALETTE.lavaBase],
    variant,
    3901,
  );

  for (let i = 0; i < 10; i++) {
    const x = Math.floor(hash01(i, variant, 3911) * TILE_SIZE);
    const y = Math.floor(hash01(i, variant, 3912) * TILE_SIZE);
    const length = 2 + Math.floor(hash01(i, variant, 3913) * 4);

    px(ctx, x, y, length, 1, TILE_PALETTE.lavaMid);
    px(ctx, x, y, 1, 1, TILE_PALETTE.lavaHot);
  }
}

// --- Sombra ------------------------------------------------------------------

/**
 * Sombra projetada no chão por parede, árvore e afloramento.
 *
 * Camada separada da transição porque as duas coexistem: a beira de uma estrada que
 * passa rente a um muro precisa da borda de terra **e** da sombra do muro, e uma camada
 * de tilemap só desenha um quadro por célula.
 */
function buildShade(scene: Phaser.Scene): void {
  if (scene.textures.exists(SHADE_TEXTURE)) return;

  const canvas = createCanvas(TILE_SIZE * SHADE_FRAMES, TILE_SIZE);
  const ctx = context2d(canvas);

  for (let mask = 1; mask < SHADE_FRAMES; mask++) {
    ctx.save();
    ctx.translate(mask * TILE_SIZE, 0);

    if ((mask & SHADE_N) !== 0) {
      for (let y = 0; y < 8; y++) {
        ctx.globalAlpha = 0.42 * (1 - y / 8);
        px(ctx, 0, y, TILE_SIZE, 1, '#000000');
      }
    }

    if ((mask & SHADE_W) !== 0) {
      for (let x = 0; x < 6; x++) {
        ctx.globalAlpha = 0.3 * (1 - x / 6);
        px(ctx, x, 0, 1, TILE_SIZE, '#000000');
      }
    }

    // A quina só escurece quando **nenhum** dos dois lados está presente: com um deles,
    // a faixa já cobriu o canto, e somar a quina deixaria um borrão preto na esquina.
    if ((mask & SHADE_NW) !== 0 && (mask & (SHADE_N | SHADE_W)) === 0) {
      for (let y = 0; y < 7; y++) {
        for (let x = 0; x < 7; x++) {
          const fade = 1 - Math.hypot(x, y) / 7;
          if (fade <= 0) continue;
          ctx.globalAlpha = 0.34 * fade;
          px(ctx, x, y, 1, 1, '#000000');
        }
      }
    }

    ctx.globalAlpha = 1;
    ctx.restore();
  }

  addGridTexture(scene, SHADE_TEXTURE, canvas, TILE_SIZE, TILE_SIZE, SHADE_FRAMES, 1);
}

// --- Vegetação e pedra -------------------------------------------------------

/** Altura do canvas em que a vegetação é desenhada: dois tiles, o de baixo e o de cima. */
const PROP_CANVAS_HEIGHT = TILE_SIZE * 2;

/**
 * Textura da vegetação, em duas metades por peça.
 *
 * Cada obstáculo é desenhado inteiro num canvas de 32×64 — com o chão na base — e depois
 * fatiado: a metade de baixo vai para a linha `prop*2`, a de cima para `prop*2+1`. Quem
 * monta o cenário põe a primeira na célula do obstáculo e a segunda na célula acima.
 */
function buildProps(scene: Phaser.Scene): void {
  if (scene.textures.exists(PROPS_TEXTURE)) return;

  const canvas = createCanvas(TILE_SIZE * VARIANTS, TILE_SIZE * PROP_COUNT * 2);
  const ctx = context2d(canvas);

  const drawers: Record<Prop, (c: CanvasRenderingContext2D, v: number) => void> = {
    [Prop.Tree]: drawTree,
    [Prop.Rock]: drawRock,
    [Prop.Bush]: drawBush,
    [Prop.Stalagmite]: drawStalagmite,
    [Prop.Crystal]: drawCrystal,
    [Prop.Mushroom]: drawMushroom,
    [Prop.Web]: drawWeb,
    [Prop.Boat]: drawBoat,
    [Prop.Counter]: drawCounter,
    [Prop.Fence]: drawFence,
    [Prop.Crate]: drawCrate,
    [Prop.Lamp]: drawLamp,
    [Prop.Sign]: drawSign,
  };

  for (const prop of Object.values(Prop)) {
    for (let variant = 0; variant < VARIANTS; variant++) {
      const tall = createCanvas(TILE_SIZE, PROP_CANVAS_HEIGHT);
      drawers[prop](context2d(tall), variant);

      // Metade de baixo (a célula do obstáculo) e metade de cima (a célula acima).
      ctx.drawImage(
        tall,
        0, TILE_SIZE, TILE_SIZE, TILE_SIZE,
        variant * TILE_SIZE, (prop * 2) * TILE_SIZE, TILE_SIZE, TILE_SIZE,
      );
      ctx.drawImage(
        tall,
        0, 0, TILE_SIZE, TILE_SIZE,
        variant * TILE_SIZE, (prop * 2 + 1) * TILE_SIZE, TILE_SIZE, TILE_SIZE,
      );
    }
  }

  addGridTexture(
    scene,
    PROPS_TEXTURE,
    canvas,
    TILE_SIZE,
    TILE_SIZE,
    VARIANTS,
    PROP_COUNT * 2,
  );
}

/**
 * Folhagem em bandas de borda dura.
 *
 * Cada banda é a **própria silhueta** deslocada para o noroeste e recortada pela original:
 * o limite entre luz e sombra acompanha a forma exatamente, porque é a forma. É o mesmo
 * recurso do `celMass` dos personagens, e é o que dá a leitura de folhagem de desenho
 * japonês — regiões chapadas separadas por um limite nítido, em vez de degradê radial.
 *
 * ## A luz de contorno vai atrás, e não por cima
 *
 * A tentação é traçar a borda com `stroke()`. Não funciona aqui: a silhueta é um agregado
 * de lóbulos, e `stroke` desenha o contorno de **cada** lóbulo — inclusive os de dentro,
 * que a união escondia. O que sai é uma coroa de argolas, e não uma árvore. A luz de borda
 * é então uma cópia da silhueta um pouco deslocada, pintada **antes** de tudo: o que dela
 * sobra descoberto é exatamente a beira voltada para o sol.
 */
function leafBands(
  ctx: CanvasRenderingContext2D,
  shape: Shape,
  bands: readonly (readonly [string, number, number])[],
  base: string,
  rim: string,
): void {
  ctx.save();
  ctx.translate(-1.5, -2);
  ctx.fillStyle = rim;
  shape(ctx);
  ctx.fill();
  ctx.restore();

  ctx.fillStyle = base;
  shape(ctx);
  ctx.fill();

  for (const [color, dx, dy] of bands) {
    clipped(ctx, shape, (c) => {
      c.translate(dx, dy);
      c.fillStyle = color;
      shape(c);
      c.fill();
    });
  }
}

/** Sombra elíptica no pé de qualquer peça. É o que assenta o objeto no chão. */
function groundShadow(ctx: CanvasRenderingContext2D, cx: number, ground: number, rx: number): void {
  ctx.globalAlpha = 0.32;
  ellipse(ctx, cx, ground - 1, rx, rx * 0.38, '#000000');
  ctx.globalAlpha = 1;
}

/**
 * Árvore.
 *
 * Duas silhuetas, escolhidas pela variação: copa redonda e conífera. Ter as duas no mesmo
 * bosque é o que impede que uma floresta grande vire um carimbo repetido — e é barato,
 * porque a diferença está em como as folhas são empilhadas, não em duas artes separadas.
 *
 * A luz vem do noroeste, como em todo o resto do cenário: o lado claro fica em cima e à
 * esquerda, a sombra desce para a direita.
 */
function drawTree(ctx: CanvasRenderingContext2D, variant: number): void {
  // Deslocamento por variação: uma árvore sempre no centro da célula transforma um bosque
  // numa plantação em linha. Três pixels para cada lado bastam para o olho parar de ver a
  // grade — e não chegam a tirar o tronco do tile, que é o que a colisão promete.
  const cx = TILE_SIZE / 2 + Math.round((hash01(variant, 1, 1401) - 0.5) * 7);
  const ground = PROP_CANVAS_HEIGHT - 2 - Math.round(hash01(variant, 2, 1403) * 3);
  const pine = variant % 3 === 2;
  const scale = 1 + hash01(variant, 7, 1501) * 0.25;

  groundShadow(ctx, cx, ground, 11);

  const trunkHeight = Math.round((pine ? 13 : 17) * scale);
  const trunkTop = ground - trunkHeight;

  px(ctx, cx - 3, trunkTop, 6, trunkHeight, TILE_PALETTE.trunk);
  px(ctx, cx + 1, trunkTop, 2, trunkHeight, TILE_PALETTE.trunkShade);
  px(ctx, cx - 3, trunkTop, 1, trunkHeight, '#75552f');
  // Casca: três traços curtos que quebram a coluna lisa.
  for (let i = 0; i < 3; i++) {
    const y = trunkTop + 2 + Math.round(hash01(i, variant, 1421) * (trunkHeight - 4));
    px(ctx, cx - 2, y, 2, 1, TILE_PALETTE.trunkShade);
  }
  // Raízes: dois pixels para cada lado no pé do tronco. Sem elas o tronco parece um poste
  // enfiado no chão.
  px(ctx, cx - 6, ground - 2, 4, 2, TILE_PALETTE.trunkShade);
  px(ctx, cx + 3, ground - 2, 4, 2, TILE_PALETTE.trunkShade);

  if (pine) {
    for (let layer = 0; layer < 5; layer++) {
      const y = trunkTop - Math.round((layer * 7 + 1) * scale);
      const width = Math.round((15 - layer * 2.4) * scale);

      // Saia da conífera, em triângulo e não em elipse: o que diz "pinheiro" é a linha
      // reta descendo do eixo para a ponta do galho. Elipse empilhada dava um pinheiro de
      // bolinhas, que é a silhueta de qualquer outra árvore.
      const skirt = (dy: number, w: number): Shape => (c) => {
        c.beginPath();
        c.moveTo(cx, y - 7 + dy);
        c.lineTo(cx + w, y + 2 + dy);
        c.lineTo(cx + w * 0.6, y + 1 + dy);
        c.lineTo(cx + w * 0.3, y + 3.5 + dy);
        c.lineTo(cx, y + 1.5 + dy);
        c.lineTo(cx - w * 0.3, y + 3.5 + dy);
        c.lineTo(cx - w * 0.6, y + 1 + dy);
        c.lineTo(cx - w, y + 2 + dy);
        c.closePath();
      };

      leafBands(ctx, skirt(0, width), [
        [TILE_PALETTE.pineBase, -0.5, -1.5],
        [TILE_PALETTE.pineLight, -2.5, -3.5],
      ], TILE_PALETTE.pineDark, TILE_PALETTE.pineRim);
    }

    const tip = trunkTop - Math.round(36 * scale);
    ellipse(ctx, cx, tip + 5, 3.5, 5, TILE_PALETTE.pineBase);
    px(ctx, cx - 1, tip + 1, 2, 5, TILE_PALETTE.pineRim);
    return;
  }

  const crownR = Math.round(17 * scale);
  const crownY = trunkTop - Math.round(crownR * 0.55);

  /**
   * Copa em bandas de borda dura, e não em elipses empilhadas.
   *
   * A versão anterior desenhava seis elipses concêntricas, cada uma menor e mais clara,
   * deslocadas para o noroeste. De longe funcionava; de perto era um degradê radial, e
   * degradê é exatamente o que a folhagem de desenho japonês **não** tem. Lá a copa é uma
   * silhueta recortada com duas ou três regiões chapadas separadas por um limite nítido —
   * o olho lê "massa de folhas iluminada de um lado", não "esfera verde".
   *
   * A silhueta sai de `cloud`, que é um agregado de lóbulos: o contorno já nasce
   * recortado, então não é preciso salpicar pixels na borda depois para fingir tufos.
   */
  const shape: Shape = (c) => {
    c.beginPath();
    for (let i = 0; i < 7; i++) {
      const angle = (i / 7) * TAU;
      const jitter = 0.8 + hash01(i, variant, 1611) * 0.42;
      const lx = cx + Math.cos(angle) * crownR * 0.6;
      const ly = crownY + Math.sin(angle) * crownR * 0.52;
      const lr = crownR * 0.5 * jitter;
      c.moveTo(lx + lr, ly);
      c.arc(lx, ly, lr, 0, TAU);
    }
    c.moveTo(cx + crownR * 0.7, crownY);
    c.ellipse(cx, crownY, crownR * 0.7, crownR * 0.62, 0, 0, TAU);
  };

  leafBands(ctx, shape, [
    [TILE_PALETTE.leafBase, -1, -2],
    [TILE_PALETTE.leafMid, -3, -5],
    [TILE_PALETTE.leafLight, -6, -9],
  ], TILE_PALETTE.leafDark, TILE_PALETTE.leafRim);

  // Um punhado de folhas soltas na região clara: o brilho pontual que faz a copa parecer
  // ter folhas em vez de ser uma chapa verde.
  for (let i = 0; i < 9; i++) {
    const angle = hash01(i, variant, 1601) * TAU;
    const radius = crownR * (0.3 + hash01(i, variant, 1602) * 0.45);
    px(
      ctx,
      Math.round(cx - 4 + Math.cos(angle) * radius),
      Math.round(crownY - 6 + Math.sin(angle) * radius * 0.7),
      2,
      2,
      TILE_PALETTE.leafHighlight,
    );
  }
}

/**
 * Afloramento de pedra.
 *
 * Desenhado por **facetas**, e não por elipses empilhadas. A primeira versão usava três
 * elipses com um brilho oval grande no alto de cada uma, e o resultado parecia um crânio:
 * duas manchas claras arredondadas sobre um fundo escuro é exatamente a leitura que o
 * olho dá a um rosto. Pedra tem quina — o que a define é a quebra entre uma face que pega
 * luz e outra que não pega, e não um degradê.
 */
function drawRock(ctx: CanvasRenderingContext2D, variant: number): void {
  const cx = TILE_SIZE / 2 + Math.round((hash01(variant, 1, 1701) - 0.5) * 6);
  const ground = PROP_CANVAS_HEIGHT - 2;
  const width = 11 + Math.round(hash01(variant, 3, 1703) * 5);
  const height = 12 + Math.round(hash01(variant, 4, 1705) * 7);

  groundShadow(ctx, cx, ground, width * 0.85);

  const top = ground - height;

  // Silhueta: um polígono irregular fechado, sorteado por variação. Os vértices ficam
  // num anel achatado, então a pedra é sempre mais larga que alta — que é como um bloco
  // assentado no chão se apresenta.
  const points: [number, number][] = [];
  const sides = 7;
  for (let i = 0; i < sides; i++) {
    const angle = (i / sides) * TAU - Math.PI / 2;
    const radius = 0.72 + hash01(i, variant, 1707) * 0.36;
    points.push([
      cx + Math.cos(angle) * width * radius,
      top + height * 0.5 + Math.sin(angle) * height * 0.55 * radius,
    ]);
  }

  const fillPolygon = (color: string, offsetX = 0, offsetY = 0, scale = 1): void => {
    ctx.fillStyle = color;
    ctx.beginPath();
    points.forEach(([x, y], index) => {
      const px0 = cx + (x - cx) * scale + offsetX;
      const py0 = top + height * 0.5 + (y - top - height * 0.5) * scale + offsetY;
      if (index === 0) ctx.moveTo(px0, py0);
      else ctx.lineTo(px0, py0);
    });
    ctx.closePath();
    ctx.fill();
  };

  fillPolygon(TILE_PALETTE.rockDeep, 0, 0, 1.12);
  fillPolygon(TILE_PALETTE.rockShade);
  // Face iluminada: o mesmo polígono encolhido e puxado para o noroeste. A faixa de
  // sombra que sobra na borda inferior direita é o que dá volume.
  fillPolygon(TILE_PALETTE.rockBase, -1, -2, 0.86);
  fillPolygon(TILE_PALETTE.rockLight, -2, -4, 0.5);

  // Fissuras: traços que descem da quina iluminada para a sombra.
  for (let i = 0; i < 2; i++) {
    const x = cx - 4 + Math.floor(hash01(i, variant, 1801) * 9);
    const y = top + 3 + Math.floor(hash01(i, variant, 1802) * 4);
    const length = 4 + Math.floor(hash01(i, variant, 1803) * 4);
    for (let step = 0; step < length; step++) {
      px(ctx, x + Math.round(step * 0.4), y + step, 1, 1, TILE_PALETTE.rockDeep);
    }
  }

  // Cascalho no pé: assenta a pedra no terreno em vez de deixá-la pousada.
  for (let i = 0; i < 3; i++) {
    const x = cx - width + Math.floor(hash01(i, variant, 1811) * width * 2);
    px(ctx, x, ground - 2, 2, 1, TILE_PALETTE.rockShade);
  }
}

/** Moita: uma massa baixa de folhas, sem tronco visível. Mesmas bandas da copa. */
function drawBush(ctx: CanvasRenderingContext2D, variant: number): void {
  const cx = TILE_SIZE / 2;
  const ground = PROP_CANVAS_HEIGHT - 2;
  const radius = 10 + Math.round(hash01(variant, 5, 1901) * 3);

  groundShadow(ctx, cx, ground, radius);

  const top = ground - radius;
  const shape: Shape = (c) => {
    c.beginPath();
    for (let i = 0; i < 5; i++) {
      const angle = (i / 5) * TAU;
      const jitter = 0.8 + hash01(i, variant, 1905) * 0.4;
      const lx = cx + Math.cos(angle) * radius * 0.58;
      const ly = top + Math.sin(angle) * radius * 0.45;
      const lr = radius * 0.5 * jitter;
      c.moveTo(lx + lr, ly);
      c.arc(lx, ly, lr, 0, TAU);
    }
    c.moveTo(cx + radius * 0.68, top);
    c.ellipse(cx, top, radius * 0.68, radius * 0.55, 0, 0, TAU);
  };

  leafBands(ctx, shape, [
    [TILE_PALETTE.leafBase, -1, -1.5],
    [TILE_PALETTE.leafMid, -3, -4],
    [TILE_PALETTE.leafLight, -5, -6.5],
  ], TILE_PALETTE.leafDark, TILE_PALETTE.leafRim);

  for (let i = 0; i < 7; i++) {
    const angle = hash01(i, variant, 1911) * TAU;
    const distance = radius * (0.25 + hash01(i, variant, 1912) * 0.4);
    px(
      ctx,
      Math.round(cx - 3 + Math.cos(angle) * distance),
      Math.round(top - 4 + Math.sin(angle) * distance * 0.7),
      2,
      2,
      TILE_PALETTE.leafHighlight,
    );
  }

  // Frutinhas em uma variação: um ponto de cor quente num mar de verde puxa o olho e faz
  // a moita parecer planta, não mancha.
  if (variant % 4 === 2) {
    for (let i = 0; i < 4; i++) {
      const x = cx - 5 + Math.floor(hash01(i, variant, 1921) * 11);
      const y = top - 3 + Math.floor(hash01(i, variant, 1922) * 9);
      px(ctx, x, y, 2, 2, '#c4443f');
      px(ctx, x, y, 1, 1, '#e87a66');
    }
  }
}

// --- Vegetação e pedra do subsolo --------------------------------------------

/**
 * Estalagmite: o cone de pedra que só existe debaixo da terra.
 *
 * A diferença com o afloramento (`drawRock`) tem que estar na **silhueta**, e não no tom:
 * pedra solta é larga e baixa, estalagmite é estreita e alta. É isso que faz o jogador
 * saber que está numa caverna mesmo olhando um tile isolado.
 *
 * Três cones por peça, de alturas diferentes, porque estalagmite nasce em grupo — uma
 * sozinha lê como poste.
 */
function drawStalagmite(ctx: CanvasRenderingContext2D, variant: number): void {
  const cx = TILE_SIZE / 2 + Math.round((hash01(variant, 1, 4101) - 0.5) * 6);
  const ground = PROP_CANVAS_HEIGHT - 2;

  groundShadow(ctx, cx, ground, 10);

  // Do fundo para a frente: o cone de trás é o mais alto e o mais escuro, e é a ordem de
  // desenho que dá a profundidade sem nenhuma linha de contorno.
  const cones: readonly (readonly [number, number, number])[] = [
    [cx - 6, 15 + Math.round(hash01(variant, 2, 4102) * 5), 4],
    [cx + 5, 12 + Math.round(hash01(variant, 3, 4103) * 5), 3.5],
    [cx, 22 + Math.round(hash01(variant, 4, 4104) * 8), 5.5],
  ];

  for (const [x, height, halfWidth] of cones) {
    const top = ground - height;

    const cone: Shape = (c) => {
      c.beginPath();
      c.moveTo(x, top);
      // O flanco não é reto: uma quebra a meia altura para cada lado dá a silhueta de pedra
      // depositada gota a gota, que é o que uma estalagmite é.
      c.lineTo(x + halfWidth * 0.55, top + height * 0.45);
      c.lineTo(x + halfWidth, ground);
      c.lineTo(x - halfWidth, ground);
      c.lineTo(x - halfWidth * 0.6, top + height * 0.5);
      c.closePath();
    };

    ctx.fillStyle = TILE_PALETTE.rockDeep;
    cone(ctx);
    ctx.fill();

    clipped(ctx, cone, (c) => {
      c.translate(-1.5, -1);
      c.fillStyle = TILE_PALETTE.rockShade;
      cone(c);
      c.fill();
    });

    clipped(ctx, cone, (c) => {
      c.translate(-3, -2);
      c.fillStyle = TILE_PALETTE.rockBase;
      cone(c);
      c.fill();
    });

    // Fio de luz na aresta voltada para o noroeste, e a ponta molhada.
    px(ctx, Math.round(x - 1), Math.round(top), 2, 3, TILE_PALETTE.rockLight);
    px(ctx, Math.round(x - 1), Math.round(top - 1), 1, 1, TILE_PALETTE.waterSparkle);
  }
}

/**
 * Aglomerado de cristal.
 *
 * É a única peça **luminosa** do cenário depois do portal, e o desenho aposta tudo nisso: um
 * halo frio no chão, prismas de aresta dura e um núcleo quase branco. Numa caverna em que
 * todo o resto é cinza e marrom, é o que puxa o jogador para o outro lado da sala — e é
 * exatamente para isso que ele existe.
 */
function drawCrystal(ctx: CanvasRenderingContext2D, variant: number): void {
  const cx = TILE_SIZE / 2 + Math.round((hash01(variant, 1, 4201) - 0.5) * 5);
  const ground = PROP_CANVAS_HEIGHT - 3;

  // Halo no chão: o cristal ilumina o que está em volta dele. Sem isso ele lê como vidro
  // colado no chão.
  ctx.globalAlpha = 0.22;
  ellipse(ctx, cx, ground - 1, 13, 5, TILE_PALETTE.crystalLight);
  ctx.globalAlpha = 0.35;
  ellipse(ctx, cx, ground - 1, 8, 3, TILE_PALETTE.crystalGlow);
  ctx.globalAlpha = 1;

  // Base de rocha: o cristal brota da pedra, não do chão liso.
  ellipse(ctx, cx, ground - 1, 8, 3.4, TILE_PALETTE.rockShade);
  ellipse(ctx, cx - 1, ground - 2, 6, 2.6, TILE_PALETTE.rockBase);

  const shards: readonly (readonly [number, number, number, number])[] = [
    [cx - 5, 13 + Math.round(hash01(variant, 2, 4202) * 4), 3.2, -0.25],
    [cx + 5, 16 + Math.round(hash01(variant, 3, 4203) * 5), 3.6, 0.3],
    [cx, 24 + Math.round(hash01(variant, 4, 4204) * 7), 4.4, 0.05],
  ];

  for (const [x, height, halfWidth, tilt] of shards) {
    const top = ground - height;
    const tipX = x + tilt * height;

    const prism: Shape = (c) => {
      c.beginPath();
      c.moveTo(tipX, top);
      c.lineTo(x + halfWidth, ground - 2);
      c.lineTo(x, ground);
      c.lineTo(x - halfWidth, ground - 2);
      c.closePath();
    };

    ctx.fillStyle = TILE_PALETTE.crystalDeep;
    prism(ctx);
    ctx.fill();

    // Duas faces por prisma: a da esquerda pega a luz, a da direita fica na sombra. Duas
    // faces chapadas separadas por uma aresta é o que se lê como cristal — degradê leria
    // como gelatina.
    clipped(ctx, prism, (c) => {
      c.translate(-1.4, -1);
      c.fillStyle = TILE_PALETTE.crystalBase;
      prism(c);
      c.fill();
    });
    clipped(ctx, prism, (c) => {
      c.translate(-2.8, -2.5);
      c.fillStyle = TILE_PALETTE.crystalLight;
      prism(c);
      c.fill();
    });

    px(ctx, Math.round(tipX - 1), Math.round(top), 2, 4, TILE_PALETTE.crystalGlow);
    px(ctx, Math.round(tipX - 1), Math.round(top - 1), 1, 2, '#ffffff');
  }
}

/**
 * Cogumelo de caverna, do tamanho de uma pessoa.
 *
 * Chapéu quente sobre pé pálido, e o chapéu é o que bloqueia: a peça precisa ler como
 * obstáculo mesmo sem contorno, e é a massa larga em cima que faz isso. Os pontos claros no
 * chapéu são o acento que o identifica de relance — sem eles, é uma pedra arredondada.
 */
function drawMushroom(ctx: CanvasRenderingContext2D, variant: number): void {
  const cx = TILE_SIZE / 2 + Math.round((hash01(variant, 1, 4301) - 0.5) * 6);
  const ground = PROP_CANVAS_HEIGHT - 2;

  groundShadow(ctx, cx, ground, 10);

  // Dois cogumelos menores atrás do principal: um sozinho no meio do tile lê como poste com
  // guarda-chuva. Em moita, lê como cogumelo.
  const caps: readonly (readonly [number, number, number])[] = [
    [cx - 7, 12, 6],
    [cx + 7, 10, 5],
    [cx, 20, 10],
  ];

  for (const [x, height, capWidth] of caps) {
    const top = ground - height;

    // Pé, mais largo embaixo.
    px(ctx, x - 2, top, 4, height, TILE_PALETTE.mushroomStem);
    px(ctx, x + 1, top, 2, height, TILE_PALETTE.mushroomStemShade);
    px(ctx, x - 3, ground - 3, 6, 3, TILE_PALETTE.mushroomStem);
    px(ctx, x - 3, ground - 1, 6, 1, TILE_PALETTE.mushroomStemShade);

    // Chapéu: meia elipse com a borda inferior recolhida, mais um vinco de luz.
    ellipse(ctx, x, top + 1, capWidth, capWidth * 0.62, TILE_PALETTE.mushroomCap);
    ellipse(
      ctx,
      x - capWidth * 0.2,
      top,
      capWidth * 0.78,
      capWidth * 0.46,
      TILE_PALETTE.mushroomCapLight,
    );
    px(ctx, x - capWidth, top + 1, capWidth * 2, 2, celShadow(TILE_PALETTE.mushroomCap, 0.3));

    // Manchas: poucas, e nunca no centro exato — simetria em mancha lê como padrão.
    for (let i = 0; i < 3; i++) {
      const sx = x + Math.round((hash01(i, variant, 4311) - 0.5) * capWidth * 1.4);
      const sy = top - 1 + Math.round(hash01(i, variant, 4312) * 3);
      px(ctx, sx, sy, 2, 2, TILE_PALETTE.mushroomSpot);
    }
  }
}

/**
 * Teia.
 *
 * Não é uma peça sólida: é um **véu** que ocupa o tile, e o desenho é a teia clássica de
 * raios com fios em espiral. Ela bloqueia — é o que faz um ninho ser um labirinto em vez de
 * uma sala com aranhas —, e por isso precisa ser densa o bastante para ler como barreira e
 * translúcida o bastante para não virar uma parede branca.
 */
function drawWeb(ctx: CanvasRenderingContext2D, variant: number): void {
  const cx = TILE_SIZE / 2;
  // O centro da teia fica na metade de cima da peça: teia se prende no alto, e uma no chão
  // leria como lençol.
  const cy = TILE_SIZE + 6;
  const radius = 20 + Math.round(hash01(variant, 1, 4401) * 6);
  const spokes = 8;

  ctx.globalAlpha = 0.5;

  for (let i = 0; i < spokes; i++) {
    const angle = (i / spokes) * TAU + hash01(i, variant, 4402) * 0.15;

    for (let step = 2; step < radius; step += 1) {
      const x = Math.round(cx + Math.cos(angle) * step);
      const y = Math.round(cy + Math.sin(angle) * step * 0.85);
      px(ctx, x, y, 1, 1, TILE_PALETTE.webThread);
    }
  }

  // Espirais: arcos entre raios vizinhos, com o vão crescendo para fora — é essa progressão
  // que o olho lê como teia em vez de como alvo.
  for (let ring = 1; ring <= 5; ring++) {
    const step = radius * (ring / 6);

    for (let i = 0; i < spokes; i++) {
      const from = (i / spokes) * TAU;
      const to = ((i + 1) / spokes) * TAU;

      for (let t = 0; t <= 1; t += 0.12) {
        // A corda do arco cede um pouco para dentro: fio de teia é curva, não polígono.
        const angle = from + (to - from) * t;
        const sag = 1 - Math.sin(t * Math.PI) * 0.12;
        const x = Math.round(cx + Math.cos(angle) * step * sag);
        const y = Math.round(cy + Math.sin(angle) * step * sag * 0.85);
        px(ctx, x, y, 1, 1, ring > 3 ? TILE_PALETTE.webShade : TILE_PALETTE.webThread);
      }
    }
  }

  ctx.globalAlpha = 0.8;
  // Nó central e dois fios de sustentação subindo para as quinas.
  ellipse(ctx, cx, cy, 2, 1.6, TILE_PALETTE.webThread);
  for (const dx of [-1, 1]) {
    for (let step = 0; step < radius; step += 2) {
      px(
        ctx,
        Math.round(cx + dx * step * 0.7),
        Math.round(cy - step * 0.9),
        1,
        1,
        TILE_PALETTE.webShade,
      );
    }
  }
  ctx.globalAlpha = 1;
}

/**
 * O barco atracado no cais (PRD §15.8).
 *
 * ## Por que ele ocupa as duas metades da peça
 *
 * Um casco desenhado dentro de um tile de 32 px é um bote — e um bote não anuncia uma
 * travessia de trezentos tiles até outro clima. A peça usa as duas células, como a árvore:
 * o casco fica na célula do tile e o **mastro com a vela** sobe para a célula de trás, que
 * continua sendo água. É a vela que se enxerga de longe, e é ela que diz "daqui se navega"
 * antes de o barqueiro caber na tela.
 *
 * ## O casco não é um retângulo com as pontas cortadas
 *
 * Ele é traçado por uma curva: a meia-largura sai de um seno ao longo do comprimento, então
 * a proa afina, o bojo enche no meio e a popa fecha larga. Um retângulo com quina lixada é
 * exatamente a leitura que o resto deste bloco existe para não produzir — e num casco ela é
 * pior, porque barco é a forma curva por excelência.
 *
 * A variação muda o tom da vela e a inclinação do mastro, e não o tamanho: cinco cais do
 * mesmo mundo com cinco barcos de tamanhos diferentes leriam como cinco classes de navio,
 * e o que a linha promete é sempre a mesma travessia.
 */
function drawBoat(ctx: CanvasRenderingContext2D, variant: number): void {
  const cx = TILE_SIZE / 2;
  /** A linha d'água. O casco afunda dois pixels abaixo dela, e é o que o faz flutuar. */
  const water = PROP_CANVAS_HEIGHT - 5;
  const bow = water - 25;
  /**
   * Comprimento e boca, e a proporção entre os dois é o desenho.
   *
   * Três por dois. A primeira versão era vinte e dois por dezenove — quase um quadrado —, e
   * o que se via na tela era uma casca de noz com um pano em cima. Casco é a forma alongada
   * por excelência, e é do comprimento contra a boca que o olho tira "embarcação" antes de
   * reconhecer qualquer detalhe.
   */
  const length = 26;

  // A sombra do casco na água, e não a elipse de chão das outras peças: no mar o que há
  // debaixo do objeto é reflexo, então ela é mais larga, mais fraca e sem borda.
  ctx.globalAlpha = 0.22;
  ellipse(ctx, cx, water + 4, 13, 4, '#000000');
  ctx.globalAlpha = 1;

  /** Meia-largura do casco na altura `t` do comprimento, com `0` na proa. */
  const beam = (t: number): number => 2.4 + Math.sin(Math.min(1, Math.max(0, t)) * Math.PI) * 6.2;

  const hull = (grow: number): Shape => (c) => {
    c.beginPath();
    for (let step = 0; step <= length; step++) {
      const t = step / length;
      c.lineTo(cx - beam(t) - grow, bow + step);
    }
    for (let step = length; step >= 0; step--) {
      const t = step / length;
      c.lineTo(cx + beam(t) + grow, bow + step);
    }
    c.closePath();
  };

  // Três camadas: o costado escuro, o bordo e a linha de luz do trincaniz. É o mesmo
  // empilhamento da pedra — silhueta, corpo, aresta iluminada a noroeste.
  ctx.fillStyle = TILE_PALETTE.boatHullDark;
  hull(1)(ctx);
  ctx.fill();

  ctx.fillStyle = TILE_PALETTE.boatHull;
  hull(0)(ctx);
  ctx.fill();

  clipped(ctx, hull(0), (c) => {
    c.translate(-1.5, -1);
    c.fillStyle = TILE_PALETTE.boatPlank;
    hull(-1.5)(c);
    c.fill();
  });

  // O convés: uma faixa clara no miolo, com a bancada de remador atravessada. Sem ela o
  // casco lê como uma casca de noz, e não como uma embarcação em que se embarca.
  for (let step = 4; step < length - 3; step++) {
    const t = step / length;
    const half = Math.max(0, beam(t) - 3.4);
    if (half <= 0) continue;
    px(ctx, Math.round(cx - half), bow + step, Math.round(half * 2), 1, TILE_PALETTE.boatTrim);
  }
  for (const thwart of [8, 14]) {
    const half = beam(thwart / length) - 1.5;
    px(ctx, Math.round(cx - half), bow + thwart, Math.round(half * 2), 1, TILE_PALETTE.boatHullDark);
  }

  // O mastro, inclinado para a popa, e a vela enfunada do lado de sotavento. A curva da
  // vela é uma parábola simples: reta, ela lê como bandeira pregada num poste.
  const lean = (hash01(variant, 1, 5101) - 0.5) * 2.2;
  const mastFoot = { x: cx, y: bow + 11 };
  const mastTop = { x: cx + lean, y: bow - 20 };
  const height = mastFoot.y - mastTop.y;

  ctx.fillStyle = variant % 2 === 0 ? TILE_PALETTE.boatSail : TILE_PALETTE.boatSailShade;
  ctx.beginPath();
  ctx.moveTo(mastTop.x, mastTop.y + 2);
  for (let step = 0; step <= 12; step++) {
    const t = step / 12;
    const belly = Math.sin(t * Math.PI) * 8.5;
    ctx.lineTo(mastTop.x + 1 + belly, mastTop.y + 2 + t * (height - 4));
  }
  ctx.lineTo(mastFoot.x + 1, mastFoot.y - 2);
  ctx.closePath();
  ctx.fill();

  // A dobra da vela: uma faixa de sombra rente ao mastro, que é onde o pano recolhe.
  ctx.fillStyle = TILE_PALETTE.boatSailShade;
  ctx.beginPath();
  ctx.moveTo(mastTop.x + 1, mastTop.y + 3);
  for (let step = 0; step <= 12; step++) {
    const t = step / 12;
    ctx.lineTo(mastTop.x + 1 + Math.sin(t * Math.PI) * 2.4, mastTop.y + 3 + t * (height - 5));
  }
  ctx.closePath();
  ctx.fill();

  for (let step = 0; step <= height; step++) {
    const t = step / height;
    px(ctx, Math.round(mastTop.x + lean * -t), mastTop.y + step, 1, 1, TILE_PALETTE.boatMast);
  }

  // O cabo de amarração, caindo da proa para fora do quadro: é o detalhe que diz "atracado"
  // em vez de "de passagem", e custa oito pixels.
  for (let step = 0; step < 7; step++) {
    px(ctx, cx - 5 - step, bow + 2 + Math.round(step * 0.5), 1, 1, TILE_PALETTE.ropeBase);
  }
}

// --- A mobília da cidade (PRD §15.8) -----------------------------------------
//
// As cinco peças abaixo seguem as mesmas regras do resto do arquivo — luz do noroeste,
// silhueta antes de detalhe, sombra elíptica no pé — e uma sexta que é só delas: **elas
// atravessam a divisa da célula**. Balcão, cerca e caixaria continuam na peça vizinha, e
// desenhá-las com quina fechada nos quatro lados daria uma fileira de objetos soltos onde
// deveria haver um móvel comprido. Cada uma resolve isso do jeito que a forma pede: a cerca
// emenda pela travessa, o balcão pela tampa, a caixaria pelo empilhamento.

/**
 * Veio de madeira de trabalho: fiadas escuras irregulares dentro de um retângulo.
 *
 * Existe como função porque balcão, cerca e caixaria são a mesma matéria vista de três
 * ângulos, e três cópias divergiriam no primeiro ajuste de tom — que é o mesmo argumento de
 * `fillRamp` para o solo.
 */
function woodGrain(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  variant: number,
  salt: number,
): void {
  for (let line = 1; line < h - 1; line += 2) {
    const from = x + Math.floor(hash01(line, variant, salt) * 3);
    const width = Math.max(2, w - Math.floor(hash01(line, variant, salt + 7) * 5));
    px(ctx, from, y + line, Math.min(width, w - (from - x)), 1, TILE_PALETTE.timberDark);
  }
}

/**
 * Balcão de loja: tampo de tábuas visto de cima, com mercadoria em cima dele.
 *
 * ## A primeira versão só emendava num sentido, e a tela mostrou
 *
 * Ela desenhava o móvel **de frente**: tampa clara no alto, caixão de tábuas embaixo, sombra
 * no pé. Numa fileira horizontal ficava exatamente como um balcão. E o Banco e o Mercado têm
 * porta a leste e a oeste, então o balcão deles corre no eixo **y** — e uma coluna de móveis
 * desenhados de frente é uma pilha de bancos, com a tampa clara reaparecendo a cada 32 px e um
 * vão de nove pixels entre um e o outro. Era a mesma falha do coroamento de parede antes de
 * §17.2, na peça que o jogador vê de mais perto.
 *
 * ## Superfície, e não fachada
 *
 * Esta versão desenha o que a projeção do jogo realmente mostra: um **tampo**. A madeira ocupa
 * o tile inteiro, de borda a borda nos quatro lados, com o veio no eixo x e uma junta escura no
 * contorno — que é a emenda entre uma tábua e a seguinte. Em fileira ou em coluna, o resultado
 * é a mesma bancada contínua com uma junta a cada tábua, porque não há mais topo nem pé para
 * repetir. A altura fica por conta do contorno escuro e da mercadoria.
 *
 * ## A mercadoria muda com a variação
 *
 * Um pote, um rolo de pano, e nada em uma variação de três: um objeto a cada 32 px de bancada
 * leria como mostruário de feira, e um balcão limpo lê como loja fechada.
 */
function drawCounter(ctx: CanvasRenderingContext2D, variant: number): void {
  const top = PROP_CANVAS_HEIGHT - TILE_SIZE;
  const ground = PROP_CANVAS_HEIGHT;

  // O tampo, cobrindo o tile inteiro. Sem sombra elíptica no pé: um móvel comprido não
  // projeta uma elipse a cada 32 px, e a junta escura do contorno já o assenta.
  px(ctx, 0, top, TILE_SIZE, TILE_SIZE, TILE_PALETTE.timberBase);
  woodGrain(ctx, 0, top + 2, TILE_SIZE, TILE_SIZE - 4, variant, 1601);

  // Luz do noroeste e sombra do sudeste, dentro da junta: é o que dá espessura ao tampo sem
  // desenhar face nenhuma.
  px(ctx, 1, top + 1, TILE_SIZE - 2, 2, TILE_PALETTE.timberLight);
  px(ctx, 1, top + 1, 2, TILE_SIZE - 2, celLight(TILE_PALETTE.timberBase, 0.18));
  px(ctx, 1, ground - 3, TILE_SIZE - 2, 2, TILE_PALETTE.timberDark);
  px(ctx, TILE_SIZE - 3, top + 1, 2, TILE_SIZE - 2, TILE_PALETTE.timberDark);

  // A junta: um pixel escuro nos quatro lados. É ela que emenda com a do tile vizinho e
  // aparece como a linha entre duas tábuas.
  px(ctx, 0, top, TILE_SIZE, 1, TILE_PALETTE.timberDeep);
  px(ctx, 0, ground - 1, TILE_SIZE, 1, TILE_PALETTE.timberDeep);
  px(ctx, 0, top, 1, TILE_SIZE, TILE_PALETTE.timberDeep);
  px(ctx, TILE_SIZE - 1, top, 1, TILE_SIZE, TILE_PALETTE.timberDeep);

  const cx = TILE_SIZE / 2;
  const shelf = top + 20;

  if (variant % 3 === 0) {
    // Pote de barro tampado, com a sombra dele no tampo.
    ctx.globalAlpha = 0.25;
    ellipse(ctx, cx + 1, shelf + 2, 5, 3, '#000000');
    ctx.globalAlpha = 1;
    ellipse(ctx, cx, shelf - 1, 4.5, 4.5, TILE_PALETTE.mushroomCap);
    ellipse(ctx, cx - 1.5, shelf - 2.5, 2, 2, TILE_PALETTE.mushroomCapLight);
    px(ctx, cx - 3, shelf - 6, 6, 2, TILE_PALETTE.timberDark);
  } else if (variant % 3 === 1) {
    // Rolo de pano deitado.
    ctx.globalAlpha = 0.25;
    ellipse(ctx, cx + 1, shelf + 3, 6, 3, '#000000');
    ctx.globalAlpha = 1;
    px(ctx, cx - 6, shelf - 4, 12, 7, TILE_PALETTE.boatSail);
    px(ctx, cx - 6, shelf - 4, 12, 2, celLight(TILE_PALETTE.boatSail, 0.2));
    px(ctx, cx - 6, shelf + 1, 12, 2, TILE_PALETTE.boatSailShade);
    px(ctx, cx - 6, shelf - 4, 2, 7, TILE_PALETTE.boatSailShade);
  }
}

/**
 * Cerca de madeira: travessas e ripas cruzadas, e as duas atravessam o tile.
 *
 * ## Ela precisa emendar nos dois sentidos, e o quintal é retangular
 *
 * Mesma lição do balcão, e o quintal das galinhas é onde ela aparece: um retângulo de cerca tem
 * dois lados horizontais e dois verticais. Uma cerca desenhada só com travessas horizontais
 * emenda nos lados de cima e de baixo e vira uma **escada de mão** nos dois de lado, com cada
 * travessa reaparecendo deitada a cada 32 px.
 *
 * A solução é a peça ser cruzada: duas travessas de borda a borda no eixo x e duas ripas de
 * borda a borda no eixo y. Num lado horizontal o olho segue as travessas e as ripas leem como
 * moirões; num lado vertical acontece o inverso, e é a mesma arte. É treliça, que é uma cerca
 * de quintal de verdade — e é a única peça deste arquivo desenhada para ser lida de dois jeitos.
 *
 * Ela é **vazada** de propósito: o que está dentro do quintal continua visível através dela.
 * Uma cerca opaca da altura de uma parede seria uma parede.
 */
function drawFence(ctx: CanvasRenderingContext2D, variant: number): void {
  const top = PROP_CANVAS_HEIGHT - TILE_SIZE;

  // Duas travessas no eixo x, atravessando o tile inteiro.
  for (const offset of [8, 20]) {
    const y = top + offset;
    px(ctx, 0, y, TILE_SIZE, 3, TILE_PALETTE.timberBase);
    px(ctx, 0, y, TILE_SIZE, 1, TILE_PALETTE.timberLight);
    px(ctx, 0, y + 2, TILE_SIZE, 1, TILE_PALETTE.timberDeep);
  }

  // Duas ripas no eixo y, idem. O desencontro de um pixel entre a ripa e a travessa é o que
  // faz o cruzamento ler como madeira pregada por cima, e não como uma grade fundida.
  for (const offset of [7, 21]) {
    const x = offset + (Math.round(hash01(offset, variant, 1701) * 2) - 1);
    px(ctx, x, top, 3, TILE_SIZE, TILE_PALETTE.timberBase);
    px(ctx, x, top, 1, TILE_SIZE, TILE_PALETTE.timberLight);
    px(ctx, x + 2, top, 1, TILE_SIZE, TILE_PALETTE.timberDeep);
    woodGrain(ctx, x, top + 1, 3, TILE_SIZE - 2, variant, 1705);
  }

  // A sombra das duas peças no chão, deslocada para o sudeste: sem ela a treliça flutua.
  ctx.globalAlpha = 0.22;
  for (const offset of [8, 20]) px(ctx, 1, top + offset + 3, TILE_SIZE - 1, 1, '#000000');
  for (const offset of [7, 21]) px(ctx, offset + 3, top + 1, 1, TILE_SIZE - 1, '#000000');
  ctx.globalAlpha = 1;
}

/**
 * Caixaria: duas caixas e um barril, empilhados contra a parede.
 *
 * O empilhamento é o desenho. Uma caixa sozinha centrada no tile lê como objeto de puzzle;
 * duas de tamanhos diferentes com um barril atrás leem como **carga**, que é o que uma loja e
 * um cais têm encostado na parede. A pilha encosta na borda de cima do tile de baixo e
 * transborda para a metade de cima da peça, então uma fileira de caixaria ao longo de uma
 * parede forma um volume contínuo em vez de uma sequência de cubos.
 */
function drawCrate(ctx: CanvasRenderingContext2D, variant: number): void {
  const ground = PROP_CANVAS_HEIGHT - 3;

  ctx.globalAlpha = 0.3;
  ellipse(ctx, TILE_SIZE / 2, ground, 13, 4, '#000000');
  ctx.globalAlpha = 1;

  /** Uma caixa: corpo, tampa iluminada, aro de ferro e o veio. */
  const box = (x: number, y: number, w: number, h: number): void => {
    px(ctx, x, y, w, h, TILE_PALETTE.timberDeep);
    px(ctx, x + 1, y + 1, w - 2, h - 2, TILE_PALETTE.timberBase);
    px(ctx, x + 1, y + 1, w - 2, 2, TILE_PALETTE.timberLight);
    woodGrain(ctx, x + 1, y + 3, w - 2, h - 5, variant, 1801);
    // A cinta em X, que é o que distingue caixa de bloco de madeira.
    for (let step = 1; step < Math.min(w, h) - 2; step++) {
      px(ctx, x + step, y + step, 1, 1, TILE_PALETTE.timberDark);
      px(ctx, x + w - 1 - step, y + step, 1, 1, TILE_PALETTE.timberDark);
    }
  };

  // O barril atrás, e as duas caixas na frente. A ordem de desenho é a ordem de
  // profundidade: o que está atrás é desenhado primeiro e fica parcialmente coberto.
  const barrel = { x: TILE_SIZE - 14, y: ground - 21 };
  px(ctx, barrel.x, barrel.y, 11, 18, TILE_PALETTE.timberDark);
  px(ctx, barrel.x + 1, barrel.y + 1, 9, 16, TILE_PALETTE.timberBase);
  px(ctx, barrel.x + 1, barrel.y + 1, 3, 16, TILE_PALETTE.timberLight);
  ellipse(ctx, barrel.x + 5.5, barrel.y + 1.5, 4.5, 2, TILE_PALETTE.timberLight);
  for (const hoop of [4, 9, 14]) {
    px(ctx, barrel.x, barrel.y + hoop, 11, 1, TILE_PALETTE.ironBase);
  }

  box(2, ground - 13, 14, 13);
  box(10, ground - 22, 10, 9);
}

/**
 * Lampião de rua: poste de ferro com a lanterna acesa no alto.
 *
 * ## Dois terços dele moram no tile de cima
 *
 * É a peça mais alta da cidade, e é por isso que ela existe: numa rua reta de calçada, o
 * lampião é a única coisa vertical, e é por ele que o olho mede a direção da via e a
 * distância já percorrida. Um poste que caiba em 32 px é um marco de calçada.
 *
 * ## A luz não ilumina, e o desenho assume isso
 *
 * A superfície não tem sistema de luz (§15.6 pôs luz só no subsolo), então o halo é
 * **desenhado** e não calculado: um disco quente de opacidade baixa em volta do vidro, que
 * some em dois pixels. Isso lê como chama à distância e não promete iluminação que o
 * cenário não vai entregar quando a noite não existe.
 */
function drawLamp(ctx: CanvasRenderingContext2D, variant: number): void {
  const cx = TILE_SIZE / 2;
  const ground = PROP_CANVAS_HEIGHT - 3;
  const glass = { x: cx, y: ground - 40 };

  groundShadow(ctx, cx, ground, 5);

  // Base alargada: um poste que nasce da largura do fuste parece fincado no chão sem pé.
  px(ctx, cx - 4, ground - 4, 8, 4, TILE_PALETTE.lampPost);
  px(ctx, cx - 4, ground - 4, 8, 1, TILE_PALETTE.lampPostLight);
  px(ctx, cx - 3, ground - 6, 6, 2, TILE_PALETTE.lampPost);

  // Fuste: dois pixels de largura, com a aresta noroeste clara.
  px(ctx, cx - 1, glass.y + 5, 3, ground - glass.y - 10, TILE_PALETTE.lampPost);
  px(ctx, cx - 1, glass.y + 5, 1, ground - glass.y - 10, TILE_PALETTE.lampPostLight);

  // Halo: dois discos quentes de opacidade baixa. Desenhados antes do vidro para que o
  // vidro fique nítido por cima deles.
  ctx.globalAlpha = 0.16;
  ellipse(ctx, glass.x, glass.y + 1, 9, 9, TILE_PALETTE.lampGlass);
  ctx.globalAlpha = 0.24;
  ellipse(ctx, glass.x, glass.y + 1, 5.5, 5.5, TILE_PALETTE.lampGlass);
  ctx.globalAlpha = 1;

  // A lanterna: caixa de vidro entre o capelo e a base, com a chama dentro.
  px(ctx, glass.x - 4, glass.y - 3, 8, 9, TILE_PALETTE.lampPost);
  px(ctx, glass.x - 3, glass.y - 2, 6, 7, TILE_PALETTE.lampGlass);
  ellipse(ctx, glass.x, glass.y + 1.5, 1.8, 2.6, TILE_PALETTE.lampFlame);
  px(ctx, glass.x - 3, glass.y - 2, 1, 7, TILE_PALETTE.lampHalo);
  px(ctx, glass.x + 2, glass.y - 2, 1, 7, TILE_PALETTE.lampHalo);

  // Capelo cônico e o gancho no topo. O gancho é o que faz o poste ler como lampião de rua
  // e não como tocha em pedestal.
  px(ctx, glass.x - 5, glass.y - 5, 10, 2, TILE_PALETTE.lampPost);
  px(ctx, glass.x - 5, glass.y - 5, 10, 1, TILE_PALETTE.lampPostLight);
  px(ctx, glass.x - 3, glass.y - 7, 6, 2, TILE_PALETTE.lampPost);
  px(ctx, glass.x - 1, glass.y - 10, 2, 3, TILE_PALETTE.lampPost);
  // Uma variação em duas tem a chama mais alta: uma rua inteira de chamas idênticas lê
  // como cópia, e a chama é a única parte da peça que pode variar sem mudar a silhueta.
  if (variant % 2 === 0) ellipse(ctx, glass.x, glass.y, 1.2, 1.8, '#ffffff');
}

/**
 * Placa: tábua pendurada num poste, com o letreiro sugerido em traços.
 *
 * ## Traço, e não letra
 *
 * Escrever texto de verdade em 20 px de largura daria um borrão ilegível, e um borrão que
 * *tenta* ser texto lê pior que um que não tenta. A tábua leva então três fiadas de traços
 * curtos — a silhueta de uma linha escrita —, e o texto de verdade está no `FeatureKind.Sign`
 * que mora no mesmo tile: quem quer ler clica, e a resposta vem no chat (ver
 * `region/systems/tools.ts`). O desenho promete texto e o servidor o entrega.
 *
 * A tábua é pendurada por dois elos e fica levemente **fora do eixo** do poste, o que dá o
 * balanço de uma placa de porta de loja. Centralizada e rente, ela leria como aviso pregado.
 */
function drawSign(ctx: CanvasRenderingContext2D, variant: number): void {
  const cx = TILE_SIZE / 2;
  const ground = PROP_CANVAS_HEIGHT - 3;
  const board = { x: cx - 10, y: ground - 30, w: 21, h: 13 };

  groundShadow(ctx, cx, ground, 4);

  // Poste.
  px(ctx, cx - 1, board.y, 3, ground - board.y - 1, TILE_PALETTE.timberBase);
  px(ctx, cx - 1, board.y, 1, ground - board.y - 1, TILE_PALETTE.timberLight);
  px(ctx, cx + 1, board.y, 1, ground - board.y - 1, TILE_PALETTE.timberDeep);
  // Braço: é ele que joga a tábua para o lado e dá o desequilíbrio de placa pendurada.
  px(ctx, cx - 9, board.y, 10, 2, TILE_PALETTE.timberBase);

  // Elos.
  for (const x of [board.x + 3, board.x + board.w - 5]) {
    px(ctx, x, board.y + 2, 1, 3, TILE_PALETTE.ironBase);
  }

  // Tábua, com moldura escura e a face clara.
  px(ctx, board.x, board.y + 4, board.w, board.h, TILE_PALETTE.timberDeep);
  px(ctx, board.x + 1, board.y + 5, board.w - 2, board.h - 2, TILE_PALETTE.signBoard);
  px(ctx, board.x + 1, board.y + 5, board.w - 2, 1, celLight(TILE_PALETTE.signBoard, 0.26));
  px(ctx, board.x + 1, board.y + board.h + 1, board.w - 2, 1, celShadow(TILE_PALETTE.signBoard, 0.3));

  // As três fiadas de "escrita": traços de uma a três células, com vão entre palavras. O
  // comprimento de cada palavra sai do hash, então duas placas vizinhas não dizem a mesma
  // coisa — o que importa porque a cidade tem uma em cada porta de loja.
  for (let line = 0; line < 3; line++) {
    let x = board.x + 3;
    const y = board.y + 7 + line * 3;

    while (x < board.x + board.w - 3) {
      const word = 2 + Math.floor(hash01(x, line + variant * 3, 1901) * 4);
      px(ctx, x, y, Math.min(word, board.x + board.w - 3 - x), 1, TILE_PALETTE.signInk);
      x += word + 2;
    }
  }
}
